import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, Scan, Plus, Minus, Search, Filter, ChevronRight, 
  ArrowLeft, ArrowRight, Save, X, CheckCircle, AlertCircle, TrendingDown,
  TrendingUp, Box, Truck, ClipboardList, BarChart3, Download,
  Calendar, User, FileText, Printer, Eye, Edit2, Trash2,
  Home, Menu, LogOut, Settings, Users, ShoppingBag, Warehouse,
  MapPin, ArrowRightLeft, Building, Layers, FileSpreadsheet, Database,
  RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { AuthScreen } from './AuthScreens';

// ============================================
// MAIN WAREHOUSE APP
// ============================================
const WarehouseApp = () => {
  const { user, userProfile, signOut } = useAuth();
  const [currentScreen, setCurrentScreen] = useState('dashboard');
  const [navigationParams, setNavigationParams] = useState({});
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isCheckingRole, setIsCheckingRole] = useState(true);

  useEffect(() => {
    // Check if user has admin or warehouse role
    if (user && userProfile) {
      if (userProfile.role !== 'admin' && userProfile.role !== 'warehouse') {
        // User is not admin or warehouse, sign them out
        handleUnauthorizedAccess();
      } else {
        setIsCheckingRole(false);
      }
    } else if (user && !userProfile) {
      // Still loading profile
      setIsCheckingRole(true);
    } else {
      // No user logged in
      setIsCheckingRole(false);
    }
  }, [user, userProfile]);

  const handleUnauthorizedAccess = async () => {
    alert('Access Denied: Only warehouse staff and administrators can access the Warehouse Portal');
    await signOut();
    setIsCheckingRole(false);
  };

  const handleNavigation = (screen, params = {}) => {
    setCurrentScreen(screen);
    setNavigationParams(params);
  };

  const handleLogout = async () => {
    await signOut();
    setShowLogoutConfirm(false);
    setCurrentScreen('auth');
  };

  // ── FROZEN (Baobab cutover) ──────────────────────────────────────────────
  // Warehouse operations (receiving, counts, adjustments, transfers) moved to
  // Baobab. This app is locked so stock is never posted to the retired OnTheMoov
  // backend and split across two systems. Set FROZEN = false to restore.
  const FROZEN = true;
  if (FROZEN) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#fff', padding: 24, textAlign: 'center' }}>
        <div style={{ maxWidth: 460 }}>
          <div style={{ fontSize: 44, marginBottom: 16 }}>📦 → 🌳</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Warehouse has moved to Baobab</h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, opacity: 0.85, marginBottom: 20 }}>
            Receiving, stock counts, adjustments and transfers are now done in <b>Baobab</b>.
            This app is frozen so stock isn’t split across two systems.
          </p>
          <p style={{ fontSize: 13, opacity: 0.6 }}>If you reached this by mistake, contact your administrator.</p>
          {user && (
            <button onClick={signOut} style={{ marginTop: 24, padding: '10px 22px', fontSize: 14, fontWeight: 700, background: '#fff', color: '#0f172a', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Sign out</button>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthScreen onSuccess={() => setCurrentScreen('dashboard')} />;
  }

  if (isCheckingRole) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  const renderScreen = () => {
    switch (currentScreen) {
      case 'dashboard':
        return <InventoryDashboard onNavigate={handleNavigation} onLogout={() => setShowLogoutConfirm(true)} />;
      case 'createGRV':
        return <CreateGRVScreen onNavigate={handleNavigation} />;
      case 'grvList':
        return <GRVListScreen onNavigate={handleNavigation} />;
      case 'viewGRV':
        return <ViewGRVScreen onNavigate={handleNavigation} grvId={navigationParams.grvId} />;
      case 'stockRegister':
        return <StockRegisterScreen onNavigate={handleNavigation} />;
      case 'stockOverview':
        return <StockOverviewScreen onNavigate={handleNavigation} />;
      case 'stockAdjustment':
        return <StockAdjustmentScreen onNavigate={handleNavigation} />;
      case 'inventoryReport':
        return <InventoryReportScreen onNavigate={handleNavigation} />;
      case 'lowStock':
        return <LowStockScreen onNavigate={handleNavigation} />;
      case 'warehouseManagement':
        return <WarehouseManagementScreen onNavigate={handleNavigation} />;
      case 'stockTransfer':
        return <StockTransferScreen onNavigate={handleNavigation} />;
      case 'warehouseInventory':
        return <WarehouseInventoryScreen onNavigate={handleNavigation} warehouseId={navigationParams.warehouseId} />;
      case 'stockReconciliation':
        return <StockReconciliationDashboard onNavigate={handleNavigation} />;
      case 'grvReport':
        return <GRVReportScreen onNavigate={handleNavigation} />;
      default:
        return <InventoryDashboard onNavigate={handleNavigation} onLogout={() => setShowLogoutConfirm(true)} />;
    }
  };

  return (
    <>
      {renderScreen()}
      
      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <LogOut size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Logout</h2>
            <p className="text-gray-600 text-center mb-6">Are you sure you want to logout?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================
// INVENTORY DASHBOARD
// ============================================
const InventoryDashboard = ({ onNavigate, onLogout }) => {
  const { user, userProfile } = useAuth();
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStock: 0,
    outOfStock: 0,
    totalValue: 0,
    warehouseCount: 0,
    locationCount: 0
  });
  const [warehouses, setWarehouses] = useState([]);
  const [recentGRVs, setRecentGRVs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    // Load all data in PARALLEL instead of sequentially
    try {
      const [
        movementsResult,
        warehousesResult,
        locationCountResult,
        grvsResult
      ] = await Promise.all([
        supabase.from('stock_movements').select('product_id, movement_type, quantity'),
        supabase.from('warehouses').select('*, storage_locations(count)').order('created_at', { ascending: false }),
        supabase.from('storage_locations').select('*', { count: 'exact', head: true }),
        supabase.from('goods_received_vouchers').select('*, warehouses(name, code)').order('created_at', { ascending: false }).limit(5)
      ]);

      // Set warehouses and GRVs immediately
      setWarehouses(warehousesResult.data || []);
      setRecentGRVs(grvsResult.data || []);

      // Calculate inventory from stock_movements
      const movements = movementsResult.data || [];
      const productStockMap = {};
      movements.forEach(movement => {
        if (!productStockMap[movement.product_id]) {
          productStockMap[movement.product_id] = 0;
        }
        
        if (['receipt', 'transfer_in', 'adjustment_in'].includes(movement.movement_type)) {
          productStockMap[movement.product_id] += movement.quantity;
        } else if (['issue', 'transfer_out', 'adjustment_out'].includes(movement.movement_type)) {
          productStockMap[movement.product_id] -= movement.quantity;
        }
      });

      // Get product details for reorder levels
      const productIds = Object.keys(productStockMap);
      
      let totalProducts = 0;
      let lowStock = 0;
      let outOfStock = 0;
      let totalValue = 0;

      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, reorder_level')
          .in('id', productIds);

        products?.forEach(product => {
          const stock = productStockMap[product.id] || 0;
          
          if (stock > 0) {
            totalProducts++;
            const reorderLevel = product.reorder_level || 10;
            
            if (stock <= reorderLevel) {
              lowStock++;
            }
          } else if (stock === 0) {
            outOfStock++;
          }
        });
      }
      
      setStats({ 
        totalProducts, 
        lowStock, 
        outOfStock, 
        totalValue,
        warehouseCount: warehousesResult.data?.length || 0,
        locationCount: locationCountResult.count || 0
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Package className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Foodline Hyper</h1>
              <p className="text-sm text-gray-600">Warehouse Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-gray-900">
                {userProfile?.name || user?.email?.split('@')[0] || 'Admin'}
              </p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <button
              onClick={onLogout}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Welcome Message */}
          <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-6 text-white">
            <h2 className="text-2xl font-bold mb-2">
              Welcome back, {userProfile?.name || 'Admin'}!
            </h2>
            <p className="opacity-90">Manage your inventory across multiple warehouses</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <Package className="text-primary" size={24} />
                <span className="text-2xl font-bold text-gray-900">{stats.totalProducts}</span>
              </div>
              <p className="text-sm text-gray-600 font-medium">Total Products</p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <Warehouse className="text-blue-600" size={24} />
                <span className="text-2xl font-bold text-gray-900">{stats.warehouseCount}</span>
              </div>
              <p className="text-sm text-gray-600 font-medium">Warehouses</p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <MapPin className="text-green-600" size={24} />
                <span className="text-2xl font-bold text-gray-900">{stats.locationCount}</span>
              </div>
              <p className="text-sm text-gray-600 font-medium">Locations</p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-orange-200">
              <div className="flex items-center justify-between mb-2">
                <AlertCircle className="text-orange-600" size={24} />
                <span className="text-2xl font-bold text-orange-600">{stats.lowStock}</span>
              </div>
              <p className="text-sm text-gray-600 font-medium">Low Stock</p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-red-200">
              <div className="flex items-center justify-between mb-2">
                <X className="text-red-600" size={24} />
                <span className="text-2xl font-bold text-red-600">{stats.outOfStock}</span>
              </div>
              <p className="text-sm text-gray-600 font-medium">Out of Stock</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <button
                onClick={() => onNavigate('stockOverview')}
                className="flex flex-col items-center justify-center p-4 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Layers className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Stock Overview</span>
              </button>

              <button
                onClick={() => onNavigate('createGRV')}
                className="flex flex-col items-center justify-center p-4 bg-primary/5 hover:bg-primary/10 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Plus className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Create GRV</span>
              </button>

              <button
                onClick={() => onNavigate('stockAdjustment')}
                className="flex flex-col items-center justify-center p-4 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Edit2 className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Stock Adjust</span>
              </button>

              <button
                onClick={() => onNavigate('warehouseManagement')}
                className="flex flex-col items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Warehouse className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Warehouses</span>
              </button>

              <button
                onClick={() => onNavigate('stockTransfer')}
                className="flex flex-col items-center justify-center p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <ArrowRightLeft className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Transfer Stock</span>
              </button>

              <button
                onClick={() => onNavigate('stockRegister')}
                className="flex flex-col items-center justify-center p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <FileText className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Stock Register</span>
              </button>

              <button
                onClick={() => onNavigate('inventoryReport')}
                className="flex flex-col items-center justify-center p-4 bg-rose-50 hover:bg-rose-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-rose-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <BarChart3 className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Movement History</span>
              </button>

              <button
                onClick={() => onNavigate('stockReconciliation')}
                className="flex flex-col items-center justify-center p-4 bg-cyan-50 hover:bg-cyan-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-cyan-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Database className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">Reconciliation</span>
              </button>

              <button
                onClick={() => onNavigate('grvReport')}
                className="flex flex-col items-center justify-center p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <ClipboardList className="text-white" size={24} />
                </div>
                <span className="text-sm font-bold text-gray-900">GRV Report</span>
              </button>
            </div>
          </div>

          {/* Warehouses Overview */}
          {warehouses.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Warehouses</h3>
                <button
                  onClick={() => onNavigate('warehouseManagement')}
                  className="text-sm text-primary font-bold hover:underline"
                >
                  View All
                </button>
              </div>
              <div className="grid gap-3">
                {warehouses.slice(0, 3).map(warehouse => (
                  <button
                    key={warehouse.id}
                    onClick={() => onNavigate('warehouseInventory', { warehouseId: warehouse.id })}
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Warehouse className="text-blue-600" size={20} />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-gray-900">{warehouse.name}</p>
                        <p className="text-sm text-gray-600">{warehouse.code}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{warehouse.storage_locations?.[0]?.count || 0} Locations</p>
                      </div>
                      <ChevronRight className="text-gray-400" size={20} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recent GRVs */}
          {recentGRVs.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Recent GRVs</h3>
                <button
                  onClick={() => onNavigate('grvList')}
                  className="text-sm text-primary font-bold hover:underline"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {recentGRVs.map(grv => (
                  <button
                    key={grv.id}
                    onClick={() => onNavigate('viewGRV', { grvId: grv.id })}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <ClipboardList className="text-green-600" size={20} />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-gray-900">{grv.grv_number}</p>
                        <p className="text-sm text-gray-600">
                          {grv.warehouses?.name || 'No warehouse'} • {new Date(grv.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="text-gray-400" size={20} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ============================================
// WAREHOUSE MANAGEMENT SCREEN
// ============================================
const WarehouseManagementScreen = ({ onNavigate }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      const { data, error } = await supabase
        .from('warehouses')
        .select(`
          *,
          storage_locations (
            id,
            code,
            name,
            aisle,
            rack,
            shelf,
            bin
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWarehouses(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setLoading(false);
    }
  };

  const handleAddLocation = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setShowLocationModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Warehouse className="text-primary" size={24} />
              <h1 className="text-xl font-bold text-gray-900">Warehouse Management</h1>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-bold hover:bg-primary/90"
          >
            <Plus size={20} />
            <span className="hidden sm:inline">Add Warehouse</span>
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-4">
          {warehouses.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <Warehouse className="mx-auto text-gray-300 mb-4" size={64} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Warehouses Yet</h3>
              <p className="text-gray-600 mb-6">Create your first warehouse to start managing inventory</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90"
              >
                Create Warehouse
              </button>
            </div>
          ) : (
            warehouses.map(warehouse => (
              <div key={warehouse.id} className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="p-6 bg-gradient-to-r from-blue-50 to-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center">
                        <Warehouse className="text-white" size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{warehouse.name}</h3>
                        <p className="text-sm text-gray-600 font-medium">Code: {warehouse.code}</p>
                        {warehouse.location && (
                          <p className="text-sm text-gray-500 mt-1">{warehouse.location}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddLocation(warehouse)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-bold hover:bg-blue-200"
                    >
                      <Plus size={18} />
                      <span className="hidden sm:inline">Add Location</span>
                    </button>
                  </div>
                </div>

                {warehouse.storage_locations && warehouse.storage_locations.length > 0 && (
                  <div className="p-6 border-t border-gray-200">
                    <h4 className="text-sm font-bold text-gray-700 uppercase mb-3">Storage Locations</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {warehouse.storage_locations.map(location => (
                        <div
                          key={location.id}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <MapPin className="text-blue-600" size={20} />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-gray-900">{location.code}</p>
                            <p className="text-sm text-gray-600">{location.name}</p>
                            {(location.aisle || location.rack || location.shelf) && (
                              <p className="text-xs text-gray-500 mt-1">
                                {[location.aisle && `Aisle ${location.aisle}`, 
                                  location.rack && `Rack ${location.rack}`, 
                                  location.shelf && `Shelf ${location.shelf}`,
                                  location.bin && `Bin ${location.bin}`
                                ].filter(Boolean).join(' • ')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      {showCreateModal && (
        <CreateWarehouseModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadWarehouses();
          }}
        />
      )}

      {showLocationModal && selectedWarehouse && (
        <CreateLocationModal
          warehouse={selectedWarehouse}
          onClose={() => {
            setShowLocationModal(false);
            setSelectedWarehouse(null);
          }}
          onSuccess={() => {
            setShowLocationModal(false);
            setSelectedWarehouse(null);
            loadWarehouses();
          }}
        />
      )}
    </div>
  );
};

// ============================================
// CREATE WAREHOUSE MODAL
// ============================================
const CreateWarehouseModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    location: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('warehouses')
        .insert([formData]);

      if (error) throw error;
      onSuccess();
    } catch (error) {
      console.error('Error creating warehouse:', error);
      alert('Failed to create warehouse');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Warehouse className="text-blue-600" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Create Warehouse</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Warehouse Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
              placeholder="e.g., Main Warehouse"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Warehouse Code *</label>
            <input
              type="text"
              required
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
              placeholder="e.g., WH001"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
              placeholder="e.g., 123 Industrial Road, Centurion"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
              rows="3"
              placeholder="Optional warehouse description"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Warehouse'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// CREATE LOCATION MODAL
// ============================================
const CreateLocationModal = ({ warehouse, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    warehouse_id: warehouse.id,
    code: '',
    name: '',
    aisle: '',
    rack: '',
    shelf: '',
    bin: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('storage_locations')
        .insert([formData]);

      if (error) throw error;
      onSuccess();
    } catch (error) {
      console.error('Error creating location:', error);
      alert('Failed to create storage location');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <MapPin className="text-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add Storage Location</h2>
              <p className="text-sm text-gray-600">{warehouse.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Location Code *</label>
              <input
                type="text"
                required
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                placeholder="e.g., A1-R2-S3"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Location Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                placeholder="e.g., Front Bay"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Aisle</label>
              <input
                type="text"
                value={formData.aisle}
                onChange={(e) => setFormData({ ...formData, aisle: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                placeholder="e.g., A1"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Rack</label>
              <input
                type="text"
                value={formData.rack}
                onChange={(e) => setFormData({ ...formData, rack: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                placeholder="e.g., R2"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Shelf</label>
              <input
                type="text"
                value={formData.shelf}
                onChange={(e) => setFormData({ ...formData, shelf: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                placeholder="e.g., S3"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Bin</label>
              <input
                type="text"
                value={formData.bin}
                onChange={(e) => setFormData({ ...formData, bin: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                placeholder="e.g., B4"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Add Location'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// STOCK TRANSFER SCREEN
// ============================================
const StockTransferScreen = ({ onNavigate }) => {
  const { user } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [formData, setFormData] = useState({
    product_id: '',
    from_warehouse_id: '',
    from_location_id: '',
    to_warehouse_id: '',
    to_location_id: '',
    quantity: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [availableStock, setAvailableStock] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.from_warehouse_id) {
      loadStorageLocations(formData.from_warehouse_id, 'from');
    }
  }, [formData.from_warehouse_id]);

  useEffect(() => {
    if (formData.to_warehouse_id) {
      loadStorageLocations(formData.to_warehouse_id, 'to');
    }
  }, [formData.to_warehouse_id]);

  useEffect(() => {
    if (formData.product_id && formData.from_warehouse_id && formData.from_location_id) {
      checkAvailableStock();
    }
  }, [formData.product_id, formData.from_warehouse_id, formData.from_location_id]);

  useEffect(() => {
    // Filter products based on search term
    if (searchTerm.trim() === '') {
      setFilteredProducts([]);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const loadInitialData = async () => {
    try {
      const { data: warehousesData } = await supabase
        .from('warehouses')
        .select('*')
        .order('name');

      setWarehouses(warehousesData || []);

      // Fetch all products with pagination to handle 1000+ products
      let allProducts = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: productsData, error } = await supabase
          .from('products')
          .select('id, name, barcode, stock_code, stock_quantity')
          .order('name')
          .range(from, from + batchSize - 1);

        if (error) {
          console.error('Error fetching products batch:', error);
          break;
        }

        if (productsData && productsData.length > 0) {
          allProducts = [...allProducts, ...productsData];
          from += batchSize;
          
          // If we got fewer items than batchSize, we've reached the end
          if (productsData.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setProducts(allProducts);
      console.log(`Loaded ${allProducts.length} products for stock transfer`);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadStorageLocations = async (warehouseId, type) => {
    try {
      const { data } = await supabase
        .from('storage_locations')
        .select('*')
        .eq('warehouse_id', warehouseId)
        .order('code');

      if (type === 'from') {
        setStorageLocations(data || []);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const checkAvailableStock = async () => {
    try {
      const { data } = await supabase
        .from('inventory_positions')
        .select('quantity')
        .eq('product_id', formData.product_id)
        .eq('warehouse_id', formData.from_warehouse_id)
        .eq('location_id', formData.from_location_id)
        .single();

      setAvailableStock(data?.quantity || 0);
    } catch (error) {
      setAvailableStock(0);
    }
  };

  const handleProductSelect = (product) => {
    setFormData({ ...formData, product_id: product.id });
    setSelectedProduct(product);
    setSearchTerm(product.name);
    setShowProductDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (parseInt(formData.quantity) > availableStock) {
      alert(`Insufficient stock! Only ${availableStock} units available at source location.`);
      return;
    }

    setLoading(true);

    try {
      // Create stock transfer record
      const { data: transfer, error: transferError } = await supabase
        .from('stock_transfers')
        .insert([{
          ...formData,
          quantity: parseInt(formData.quantity),
          transferred_by: user.id,
          status: 'completed'
        }])
        .select()
        .single();

      if (transferError) throw transferError;

      // Update inventory positions
      // Reduce from source
      const { error: reduceError } = await supabase.rpc('update_inventory_position', {
        p_product_id: formData.product_id,
        p_warehouse_id: formData.from_warehouse_id,
        p_location_id: formData.from_location_id,
        p_quantity_change: -parseInt(formData.quantity)
      });

      if (reduceError) throw reduceError;

      // Add to destination
      const { error: addError } = await supabase.rpc('update_inventory_position', {
        p_product_id: formData.product_id,
        p_warehouse_id: formData.to_warehouse_id,
        p_location_id: formData.to_location_id,
        p_quantity_change: parseInt(formData.quantity)
      });

      if (addError) throw addError;

      // Get the new stock at destination after transfer
      const { data: destinationStock } = await supabase
        .from('inventory_positions')
        .select('quantity')
        .eq('product_id', formData.product_id)
        .eq('warehouse_id', formData.to_warehouse_id)
        .eq('location_id', formData.to_location_id)
        .single();

      const newDestinationQty = destinationStock?.quantity || parseInt(formData.quantity);
      const prevDestinationQty = newDestinationQty - parseInt(formData.quantity);

      // Create stock movements
      await supabase.from('stock_movements').insert([
        {
          product_id: formData.product_id,
          warehouse_id: formData.from_warehouse_id,
          location_id: formData.from_location_id,
          movement_type: 'transfer_out',
          quantity: parseInt(formData.quantity),
          previous_stock: availableStock,
          new_stock: availableStock - parseInt(formData.quantity),
          reference_type: 'TRANSFER',
          reference_id: transfer.id,
          notes: `Transfer to ${warehouses.find(w => w.id === formData.to_warehouse_id)?.name} - Location ${storageLocations.find(l => l.id === formData.to_location_id)?.code}`,
          created_by: user.id
        },
        {
          product_id: formData.product_id,
          warehouse_id: formData.to_warehouse_id,
          location_id: formData.to_location_id,
          movement_type: 'transfer_in',
          quantity: parseInt(formData.quantity),
          previous_stock: prevDestinationQty,
          new_stock: newDestinationQty,
          reference_type: 'TRANSFER',
          reference_id: transfer.id,
          notes: `Transfer from ${warehouses.find(w => w.id === formData.from_warehouse_id)?.name} - Location ${storageLocations.find(l => l.id === formData.from_location_id)?.code}`,
          created_by: user.id
        }
      ]);

      alert('Stock transfer completed successfully!');
      onNavigate('dashboard');
    } catch (error) {
      console.error('Error transferring stock:', error);
      alert('Failed to complete transfer: ' + error.message);
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="text-purple-600" size={24} />
            <h1 className="text-xl font-bold text-gray-900">Stock Transfer</h1>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-gray-200 p-6 space-y-6">
            {/* Product Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Product *</label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    required
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowProductDropdown(true);
                      if (e.target.value === '') {
                        setFormData({ ...formData, product_id: '' });
                        setSelectedProduct(null);
                      }
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    placeholder="Search by product name or barcode..."
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>
                
                {/* Dropdown with filtered products */}
                {showProductDropdown && filteredProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-300 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    {filteredProducts.map(product => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleProductSelect(product)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <div className="font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">
                          {product.stock_code && <span className="mr-3">Code: {product.stock_code}</span>}
                          {product.barcode || 'No barcode'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Show message when searching but no results */}
                {showProductDropdown && searchTerm && filteredProducts.length === 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-300 rounded-xl shadow-lg p-4">
                    <p className="text-gray-500 text-center">No products found</p>
                  </div>
                )}
              </div>
            </div>

            {/* From Section */}
            <div className="border-2 border-red-200 bg-red-50 rounded-xl p-4">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingDown className="text-red-600" size={20} />
                Transfer From
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Warehouse *</label>
                  <select
                    required
                    value={formData.from_warehouse_id}
                    onChange={(e) => setFormData({ ...formData, from_warehouse_id: e.target.value, from_location_id: '' })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none bg-white"
                  >
                    <option value="">Select Warehouse</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} ({warehouse.code})
                      </option>
                    ))}
                  </select>
                </div>

                {formData.from_warehouse_id && (
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Storage Location *</label>
                    <select
                      required
                      value={formData.from_location_id}
                      onChange={(e) => setFormData({ ...formData, from_location_id: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none bg-white"
                    >
                      <option value="">Select Location</option>
                      {storageLocations.map(location => (
                        <option key={location.id} value={location.id}>
                          {location.code} - {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {availableStock > 0 && (
                  <div className="bg-white border border-gray-300 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Available Stock</p>
                    <p className="text-2xl font-bold text-gray-900">{availableStock} units</p>
                  </div>
                )}
              </div>
            </div>

            {/* To Section */}
            <div className="border-2 border-green-200 bg-green-50 rounded-xl p-4">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="text-green-600" size={20} />
                Transfer To
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Warehouse *</label>
                  <select
                    required
                    value={formData.to_warehouse_id}
                    onChange={(e) => setFormData({ ...formData, to_warehouse_id: e.target.value, to_location_id: '' })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none bg-white"
                  >
                    <option value="">Select Warehouse</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} ({warehouse.code})
                      </option>
                    ))}
                  </select>
                </div>

                {formData.to_warehouse_id && (
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Storage Location *</label>
                    <select
                      required
                      value={formData.to_location_id}
                      onChange={(e) => setFormData({ ...formData, to_location_id: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none bg-white"
                    >
                      <option value="">Select Location</option>
                      {storageLocations.map(location => (
                        <option key={location.id} value={location.id}>
                          {location.code} - {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Quantity and Notes */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Quantity to Transfer *</label>
              <input
                type="number"
                required
                min="1"
                max={availableStock}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none text-2xl font-bold text-center"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Transfer Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                rows="3"
                placeholder="Optional notes about this transfer"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => onNavigate('dashboard')}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !availableStock}
                className="flex-1 px-6 py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 disabled:opacity-50"
              >
                {loading ? 'Transferring...' : 'Complete Transfer'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

// ============================================
// WAREHOUSE INVENTORY SCREEN
// ============================================
const WarehouseInventoryScreen = ({ onNavigate, warehouseId }) => {
  const [warehouse, setWarehouse] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadWarehouseInventory();
  }, [warehouseId]);

  const loadWarehouseInventory = async () => {
    try {
      // Load warehouse details
      const { data: warehouseData } = await supabase
        .from('warehouses')
        .select('*')
        .eq('id', warehouseId)
        .single();

      setWarehouse(warehouseData);

      // Load inventory positions
      const { data: inventoryData } = await supabase
        .from('inventory_positions')
        .select(`
          *,
          products(name, barcode, stock_code),
          storage_locations(code, name)
        `)
        .eq('warehouse_id', warehouseId)
        .order('quantity', { ascending: false });

      setInventory(inventoryData || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading inventory:', error);
      setLoading(false);
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.products?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.products?.barcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.products?.stock_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.storage_locations?.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => onNavigate('warehouseManagement')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Warehouse className="text-blue-600" size={24} />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{warehouse?.name}</h1>
              <p className="text-sm text-gray-600">{warehouse?.code}</p>
            </div>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by product, stock code, or location..."
            className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
          />
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {filteredInventory.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <Box className="mx-auto text-gray-300 mb-4" size={64} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Inventory</h3>
              <p className="text-gray-600">No products stored in this warehouse yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Product</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Stock Code</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Location</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase">Quantity</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredInventory.map(item => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-bold text-gray-900">{item.products?.name}</div>
                          <div className="text-sm text-gray-600">{item.products?.barcode || '-'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-gray-900">{item.products?.stock_code || '-'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-blue-600" />
                            <div>
                              <div className="font-bold text-gray-900">{item.storage_locations?.code}</div>
                              <div className="text-sm text-gray-600">{item.storage_locations?.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-xl font-bold text-gray-900">{item.quantity}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(item.updated_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ============================================
// CREATE GRV SCREEN (Enhanced with Warehouse/Location)
// ============================================
const CreateGRVScreen = ({ onNavigate }) => {
  const { user } = useAuth();
  const [supplier, setSupplier] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [storageLocations, setStorageLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    barcode: '',
    product: null,
    quantity: '',
    batch: '',
    expiryDate: '',
    selectionMethod: 'scan', // 'scan' or 'search'
    searchTerm: ''
  });
  const [products, setProducts] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      loadStorageLocations(selectedWarehouse);
    }
  }, [selectedWarehouse]);

  const loadInitialData = async () => {
    try {
      const { data: warehousesData } = await supabase
        .from('warehouses')
        .select('*')
        .order('name');

      setWarehouses(warehousesData || []);

      // Fetch all products with pagination to handle 1000+ products
      let allProducts = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: productsData, error } = await supabase
          .from('products')
          .select('*')
          .order('name')
          .range(from, from + batchSize - 1);

        if (error) {
          console.error('Error fetching products batch:', error);
          break;
        }

        if (productsData && productsData.length > 0) {
          allProducts = [...allProducts, ...productsData];
          from += batchSize;
          
          // If we got fewer items than batchSize, we've reached the end
          if (productsData.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setProducts(allProducts);
      
      // Log for debugging
      console.log('Loaded products:', allProducts?.length || 0);
      if (allProducts?.length === 0) {
        console.warn('No products found in database. Please add products first.');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadStorageLocations = async (warehouseId) => {
    try {
      const { data } = await supabase
        .from('storage_locations')
        .select('*')
        .eq('warehouse_id', warehouseId)
        .order('code');

      setStorageLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleScanBarcode = async () => {
    if (!currentItem.barcode.trim()) return;

    const barcodeToSearch = currentItem.barcode.trim();
    console.log('🔍 Searching for barcode:', barcodeToSearch);
    
    setScanning(true);
    try {
      // First try to find in loaded products (faster)
      const productInList = products.find(p => p.barcode === barcodeToSearch);
      console.log('📦 Found in loaded products:', productInList ? 'YES' : 'NO');
      
      if (productInList) {
        setCurrentItem({
          ...currentItem,
          product: productInList
        });
        console.log('✅ Product set:', productInList.name);
      } else {
        // Fallback to database query
        console.log('🔎 Querying database...');
        const { data: product, error } = await supabase
          .from('products')
          .select('*')
          .eq('barcode', barcodeToSearch)
          .single();

        console.log('Database result:', { product, error });

        if (error) {
          console.error('Database error:', error);
          alert('Product not found!');
        } else if (product) {
          setCurrentItem({
            ...currentItem,
            product: product
          });
          console.log('✅ Product found in DB:', product.name);
        } else {
          alert('Product not found!');
        }
      }
    } catch (error) {
      console.error('❌ Error finding product:', error);
      alert('Error searching for product: ' + error.message);
    }
    setScanning(false);
  };

  const handleAddItem = () => {
    if (!currentItem.product || !currentItem.quantity) {
      alert('Please select a product and enter quantity');
      return;
    }

    if (!selectedLocation) {
      alert('Please select a storage location');
      return;
    }

    setItems([...items, { ...currentItem, location_id: selectedLocation }]);
    setCurrentItem({
      barcode: '',
      product: null,
      quantity: '',
      batch: '',
      expiryDate: '',
      selectionMethod: 'scan',
      searchTerm: ''
    });
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSaveGRV = async () => {
    if (!supplier || !selectedWarehouse || items.length === 0) {
      alert('Please fill in all required fields and add at least one item');
      return;
    }

    setSaving(true);
    
    // Prepare items array for the database function
    const grvItems = items.map(item => ({
      product_id: item.product.id,
      location_id: item.location_id,
      quantity: parseInt(item.quantity),
      unit_price: item.product?.defaultVariant?.price || item.product?.price || 0,
      batch_number: item.batch || null,
      expiry_date: item.expiryDate || null
    }));

    try {
      const { data, error } = await supabase.rpc('process_grv', {
        p_supplier: supplier,
        p_warehouse_id: selectedWarehouse,
        p_received_by: user.email,
        p_user_id: user.id,
        p_items: grvItems
      });

      if (error) {
        console.error('GRV processing error:', error);
        alert('Error saving GRV: ' + error.message);
        setSaving(false);
        return;
      }
      
      const grvNumber = data?.[0]?.grv_number || 'GRV';
      console.log('GRV processed successfully:', grvNumber);
      alert(`GRV ${grvNumber} saved successfully!`);
      onNavigate('dashboard');
    } catch (error) {
      console.error('Error saving GRV:', error);
      alert('Error saving GRV: ' + error.message);
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <ClipboardList className="text-primary" size={24} />
              <h1 className="text-xl font-bold text-gray-900">Create GRV</h1>
            </div>
          </div>
          <button
            onClick={handleSaveGRV}
            disabled={saving || items.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 disabled:opacity-50"
          >
            <Save size={20} />
            <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save GRV'}</span>
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto pb-6">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* GRV Details */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">GRV Details</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">GRV Number</label>
                <input
                  type="text"
                  value="Auto-generated"
                  disabled
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-gray-50 text-gray-500 italic"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Supplier Name *</label>
                <input
                  type="text"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  placeholder="Enter supplier name"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Warehouse *</label>
                <select
                  value={selectedWarehouse}
                  onChange={(e) => {
                    setSelectedWarehouse(e.target.value);
                    setSelectedLocation('');
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                >
                  <option value="">Select Warehouse</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.code})
                    </option>
                  ))}
                </select>
              </div>
              {selectedWarehouse && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Default Storage Location *</label>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                  >
                    <option value="">Select Location</option>
                    {storageLocations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.code} - {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Add Item Section */}
          {selectedWarehouse && selectedLocation && (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Add Item</h3>
              <div className="space-y-4">
                {/* Method Selector */}
                <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setCurrentItem({ ...currentItem, selectionMethod: 'scan' })}
                    className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                      currentItem.selectionMethod !== 'search' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Scan className="inline mr-2" size={18} />
                    Scan/Enter Barcode
                  </button>
                  <button
                    onClick={() => setCurrentItem({ ...currentItem, selectionMethod: 'search' })}
                    className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                      currentItem.selectionMethod === 'search' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Search className="inline mr-2" size={18} />
                    Search Products
                  </button>
                </div>

                {/* Barcode Scanner */}
                {currentItem.selectionMethod !== 'search' && (
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Barcode</label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          ref={barcodeInputRef}
                          type="text"
                          value={currentItem.barcode}
                          onChange={(e) => setCurrentItem({ ...currentItem, barcode: e.target.value })}
                          onKeyPress={(e) => e.key === 'Enter' && handleScanBarcode()}
                          placeholder="Scan or type barcode and press Enter"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                          autoFocus
                        />
                      </div>
                      <button
                        onClick={handleScanBarcode}
                        disabled={scanning || !currentItem.barcode.trim()}
                        className="px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {scanning ? '...' : <Scan size={20} />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Product Search */}
                {currentItem.selectionMethod === 'search' && (
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Search & Select Product</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        value={currentItem.searchTerm || ''}
                        onChange={(e) => setCurrentItem({ ...currentItem, searchTerm: e.target.value })}
                        placeholder="Type to search products by name or barcode..."
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                      />
                    </div>
                    
                    {/* Product List */}
                    <div className="mt-2 max-h-64 overflow-y-auto border-2 border-gray-200 rounded-xl">
                      {(() => {
                        const search = (currentItem.searchTerm || '').toLowerCase();
                        const filtered = products.filter(p => {
                          if (!search) return false; // Don't show all products initially
                          return (
                            p.name.toLowerCase().includes(search) ||
                            (p.barcode && p.barcode.toLowerCase().includes(search))
                          );
                        }).slice(0, 100); // Limit to first 100 results for performance
                        
                        if (!search) {
                          return (
                            <div className="p-8 text-center text-gray-500">
                              <Search size={48} className="mx-auto mb-3 text-gray-300" />
                              <p className="font-medium">Start typing to search</p>
                              <p className="text-sm mt-1">Search by product name or barcode</p>
                            </div>
                          );
                        }
                        
                        if (filtered.length === 0) {
                          return (
                            <div className="p-8 text-center text-gray-500">
                              <Package size={48} className="mx-auto mb-3 text-gray-300" />
                              <p className="font-medium">No products found</p>
                              <p className="text-sm mt-1">Try a different search term</p>
                            </div>
                          );
                        }
                        
                        return filtered.map(product => (
                          <button
                            key={product.id}
                            onClick={() => {
                              setCurrentItem({
                                ...currentItem,
                                product: product,
                                barcode: product.barcode || '',
                                searchTerm: ''
                              });
                            }}
                            className="w-full p-4 text-left hover:bg-primary/5 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-bold text-gray-900">{product.name}</div>
                            <div className="text-sm text-gray-600">
                              {product.stock_code && <span className="mr-3">Code: {product.stock_code}</span>}
                              {product.barcode ? `Barcode: ${product.barcode}` : 'No barcode'}
                              {product.stock_quantity !== undefined && (
                                <span className="ml-3">• Stock: {product.stock_quantity}</span>
                              )}
                            </div>
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {currentItem.product && (
                  <>
                    <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                      <p className="text-sm text-green-600 font-bold mb-1">Product Found</p>
                      <p className="text-lg font-bold text-gray-900">{currentItem.product.name}</p>
                      <div className="flex gap-4 text-sm text-gray-600 mt-1">
                        {currentItem.product.stock_code && (
                          <span>Stock Code: <span className="font-medium text-gray-900">{currentItem.product.stock_code}</span></span>
                        )}
                        {currentItem.product.barcode && (
                          <span>Barcode: <span className="font-medium text-gray-900">{currentItem.product.barcode}</span></span>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Quantity *</label>
                        <input
                          type="number"
                          value={currentItem.quantity}
                          onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                          min="1"
                          placeholder="0"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Batch Number</label>
                        <input
                          type="text"
                          value={currentItem.batch}
                          onChange={(e) => setCurrentItem({ ...currentItem, batch: e.target.value })}
                          placeholder="Optional"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Expiry Date</label>
                        <input
                          type="date"
                          value={currentItem.expiryDate}
                          onChange={(e) => setCurrentItem({ ...currentItem, expiryDate: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleAddItem}
                      className="w-full px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <Plus size={20} />
                      Add Item to GRV
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Items List */}
          {items.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Items ({items.length})</h3>
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <p className="font-bold text-gray-900">{item.product.name}</p>
                      <p className="text-sm text-gray-600">
                        {item.product.stock_code && <span className="mr-3">Code: <span className="font-medium">{item.product.stock_code}</span></span>}
                        Qty: <span className="font-bold">{item.quantity}</span>
                        {item.batch && ` • Batch: ${item.batch}`}
                        {item.expiryDate && ` • Exp: ${new Date(item.expiryDate).toLocaleDateString()}`}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ============================================
// STOCK OVERVIEW SCREEN
// ============================================
const StockOverviewScreen = ({ onNavigate }) => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [inventory, searchTerm, selectedWarehouse, selectedLocation, selectedCategory, selectedSubCategory]);

  const loadData = async () => {
    try {
      // Load all warehouses
      const { data: warehousesData } = await supabase
        .from('warehouses')
        .select('*')
        .order('name');

      setWarehouses(warehousesData || []);

      // Load ALL storage locations with warehouse info
      const { data: allLocations } = await supabase
        .from('storage_locations')
        .select('*, warehouses(id, name, code)')
        .order('code');
      
      setLocations(allLocations || []);

      // Load ALL stock_movements by paginating through entire dataset
      let allMovements = [];
      let page = 0;
      const pageSize = 5000; // Large page size for efficiency
      let keepFetching = true;

      while (keepFetching) {
        const { data: batch, error: batchError } = await supabase
          .from('stock_movements')
          .select(`
            *,
            products(id, name, barcode, stock_code, category, subcategory),
            warehouses(id, name, code),
            storage_locations(id, code, name, warehouse_id)
          `)
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (batchError) {
          console.error('Error loading stock movements:', batchError);
          break;
        }

        if (batch && batch.length > 0) {
          allMovements = [...allMovements, ...batch];
          page++;
          
          // If we got less than pageSize, we've reached the end
          if (batch.length < pageSize) {
            keepFetching = false;
          }
        } else {
          keepFetching = false;
        }
      }

      const movementsData = allMovements;
      console.log('Stock movements loaded:', movementsData.length);

      if (movementsData && movementsData.length > 0) {
        // Calculate current inventory by aggregating movements at location level
        const inventoryMap = {};
        
        movementsData.forEach((movement, index) => {
          const key = `${movement.warehouse_id}-${movement.location_id}-${movement.product_id}`;
          
          if (!inventoryMap[key]) {
            inventoryMap[key] = {
              id: key,
              product_id: movement.product_id,
              warehouse_id: movement.warehouse_id,
              location_id: movement.location_id,
              quantity: 0,
              products: movement.products,
              warehouses: movement.warehouses,
              storage_locations: movement.storage_locations
            };
          }
          
          // Add receipts, subtract issues
          if (['receipt', 'transfer_in', 'adjustment_in'].includes(movement.movement_type)) {
            inventoryMap[key].quantity += movement.quantity;
          } else if (['issue', 'transfer_out', 'adjustment_out'].includes(movement.movement_type)) {
            inventoryMap[key].quantity -= movement.quantity;
          }
        });

        // Convert to array and filter out zero/negative quantities
        const calculatedInventory = Object.values(inventoryMap)
          .filter(item => item.quantity > 0)
          .sort((a, b) => b.quantity - a.quantity);

        console.log('Inventory positions calculated:', calculatedInventory.length);
        setInventory(calculatedInventory);

        // Extract categories
        const uniqueCategories = [...new Set(
          calculatedInventory.map(item => item.products?.category).filter(Boolean)
        )].sort();
        setCategories(uniqueCategories);

        const uniqueSubCategories = [...new Set(
          calculatedInventory.map(item => item.products?.subcategory).filter(Boolean)
        )].sort();
        setSubCategories(uniqueSubCategories);
      } else {
        console.log('No stock movements found');
        setInventory([]);
        setCategories([]);
        setSubCategories([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading inventory:', error);
      alert('Error loading inventory: ' + error.message);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...inventory];

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const product = item.products || item;
        return (
          product?.name?.toLowerCase().includes(search) ||
          product?.barcode?.toLowerCase().includes(search) ||
          product?.stock_code?.toLowerCase().includes(search)
        );
      });
    }

    // Warehouse filter - only apply if warehouse is selected
    if (selectedWarehouse !== 'all') {
      filtered = filtered.filter(item => {
        // Handle both inventory_positions format and fallback format
        const itemWarehouseId = item.warehouse_id || item.warehouses?.id;
        return itemWarehouseId === selectedWarehouse || String(itemWarehouseId) === String(selectedWarehouse);
      });
    }

    // Location filter - only apply if location is selected  
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(item => {
        const itemLocationId = item.location_id || item.storage_locations?.id;
        return itemLocationId === selectedLocation || String(itemLocationId) === String(selectedLocation);
      });
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => {
        const product = item.products || item;
        return product?.category === selectedCategory;
      });
    }

    // Sub-category filter
    if (selectedSubCategory !== 'all') {
      filtered = filtered.filter(item => {
        const product = item.products || item;
        return product?.subcategory === selectedSubCategory;
      });
    }

    setFilteredInventory(filtered);
  };

  const getTotalQuantity = () => {
    return filteredInventory.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getTotalItems = () => {
    return filteredInventory.length;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedWarehouse('all');
    setSelectedLocation('all');
    setSelectedCategory('all');
    setSelectedSubCategory('all');
  };

  const exportToExcel = () => {
    // Prepare data for export
    const exportData = filteredInventory.map(item => {
      const product = item.products || item;
      return {
        'Product Name': product?.name || '',
        'Stock Code': product?.stock_code || '',
        'Barcode': product?.barcode || '',
        'Category': product?.category || '',
        'Sub-Category': product?.subcategory || '',
        'Warehouse': item.warehouses?.name || 'N/A',
        'Warehouse Code': item.warehouses?.code || 'N/A',
        'Location': item.storage_locations?.code || 'N/A',
        'Location Name': item.storage_locations?.name || 'N/A',
        'Quantity': item.quantity
      };
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    ws['!cols'] = [
      { wch: 35 }, // Product Name
      { wch: 15 }, // Stock Code
      { wch: 15 }, // Barcode
      { wch: 18 }, // Category
      { wch: 18 }, // Sub-Category
      { wch: 20 }, // Warehouse
      { wch: 15 }, // Warehouse Code
      { wch: 12 }, // Location
      { wch: 20 }, // Location Name
      { wch: 10 }  // Quantity
    ];

    // Add summary row at the bottom
    const summaryRowIndex = exportData.length + 2;
    XLSX.utils.sheet_add_aoa(ws, [
      [''],
      ['Summary'],
      ['Total Items:', getTotalItems()],
      ['Total Quantity:', getTotalQuantity()],
      ['Export Date:', new Date().toLocaleString()]
    ], { origin: `A${summaryRowIndex}` });

    // Append worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Overview');

    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const filename = `Stock_Overview_${date}.xlsx`;

    // Export the file
    XLSX.writeFile(wb, filename);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Layers className="text-indigo-600" size={24} />
              <h1 className="text-xl font-bold text-gray-900">Stock Overview</h1>
            </div>
          </div>
          <button
            onClick={exportToExcel}
            disabled={filteredInventory.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-colors"
          >
            <FileSpreadsheet size={18} />
            <span className="hidden sm:inline">Export Excel</span>
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="bg-indigo-50 rounded-lg p-3">
            <p className="text-xs text-indigo-600 font-medium mb-1">Total Items</p>
            <p className="text-2xl font-bold text-indigo-900">{getTotalItems()}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <p className="text-xs text-green-600 font-medium mb-1">Total Quantity</p>
            <p className="text-2xl font-bold text-green-900">{getTotalQuantity().toLocaleString()}</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by product name or barcode..."
            className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none"
          />
        </div>

        {/* Filters */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none text-sm"
            >
              <option value="all">All Warehouses</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>{wh.name}</option>
              ))}
            </select>

            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none text-sm"
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.code} {loc.warehouses?.name ? `(${loc.warehouses.name})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none text-sm"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none text-sm"
            >
              <option value="all">All Sub-Categories</option>
              {subCategories.map(subCat => (
                <option key={subCat} value={subCat}>{subCat}</option>
              ))}
            </select>
          </div>

          {(searchTerm || selectedWarehouse !== 'all' || selectedCategory !== 'all') && (
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm transition-colors"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 p-4 pb-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {filteredInventory.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <Package className="mx-auto text-gray-300 mb-4" size={64} />
              <h3 className="text-lg font-bold text-gray-900 mb-2">No inventory found</h3>
              {(selectedWarehouse !== 'all' || selectedLocation !== 'all') ? (
                <div className="space-y-3">
                  <p className="text-gray-600">
                    No stock found in the selected warehouse/location.
                  </p>
                  <p className="text-sm text-gray-500">
                    This might mean stock hasn't been allocated to specific warehouse locations yet.
                    Try viewing "All Warehouses" to see total stock quantities.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary/90"
                  >
                    Clear Filters & View All Stock
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-gray-600 mb-4">Try adjusting your filters or search term</p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary/90"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredInventory.map(item => {
                const product = item.products || item;
                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl border-2 border-gray-200 p-4 hover:border-primary transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-1">
                          {product?.name}
                        </h3>
                        <div className="flex flex-wrap gap-x-4 text-sm text-gray-600">
                          {product?.stock_code && (
                            <span>Code: <span className="font-medium text-gray-900">{product.stock_code}</span></span>
                          )}
                          {product?.barcode && (
                            <span>Barcode: {product.barcode}</span>
                          )}
                        </div>
                        {product?.category && (
                          <div className="flex gap-2 mt-2">
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                              {product.category}
                            </span>
                            {product?.subcategory && (
                              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                                {product.subcategory}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-primary">
                          {item.quantity}
                        </p>
                        <p className="text-xs text-gray-500">units</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      {item.warehouses ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Warehouse className="text-blue-600" size={16} />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.warehouses.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.warehouses.code}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="text-green-600" size={16} />
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                {item.storage_locations?.code || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.storage_locations?.name || 'No location'}
                              </p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Package size={16} />
                          <p className="text-sm">Total Stock (Not allocated to warehouse)</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ============================================
// STOCK ADJUSTMENT SCREEN
// ============================================
const StockAdjustmentScreen = ({ onNavigate }) => {
  const { user } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [currentStock, setCurrentStock] = useState(0);
  const [physicalCount, setPhysicalCount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts([]);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  useEffect(() => {
    if (selectedWarehouse) {
      loadStorageLocations(selectedWarehouse);
    } else {
      setStorageLocations([]);
      setSelectedLocation('');
    }
  }, [selectedWarehouse]);

  useEffect(() => {
    if (selectedProduct && selectedWarehouse && selectedLocation) {
      loadCurrentStock();
    } else {
      setCurrentStock(0);
    }
  }, [selectedProduct, selectedWarehouse, selectedLocation]);

  const loadInitialData = async () => {
    try {
      const { data: warehousesData } = await supabase
        .from('warehouses')
        .select('*')
        .order('name');

      setWarehouses(warehousesData || []);

      // Fetch all products with pagination to handle 1000+ products
      let allProducts = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: productsData, error } = await supabase
          .from('products')
          .select('id, name, barcode, stock_code')
          .order('name')
          .range(from, from + batchSize - 1);

        if (error) {
          console.error('Error fetching products batch:', error);
          break;
        }

        if (productsData && productsData.length > 0) {
          allProducts = [...allProducts, ...productsData];
          from += batchSize;
          
          // If we got fewer items than batchSize, we've reached the end
          if (productsData.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setProducts(allProducts);
      console.log(`Loaded ${allProducts.length} products for stock adjustment`);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadStorageLocations = async (warehouseId) => {
    try {
      const { data } = await supabase
        .from('storage_locations')
        .select('*')
        .eq('warehouse_id', warehouseId)
        .order('code');

      setStorageLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadCurrentStock = async () => {
    try {
      // Calculate current stock from stock_movements
      const { data: movements } = await supabase
        .from('stock_movements')
        .select('movement_type, quantity')
        .eq('product_id', selectedProduct.id)
        .eq('warehouse_id', selectedWarehouse)
        .eq('location_id', selectedLocation);

      let calculatedStock = 0;
      movements?.forEach(movement => {
        if (['receipt', 'transfer_in', 'adjustment_in'].includes(movement.movement_type)) {
          calculatedStock += movement.quantity;
        } else if (['issue', 'transfer_out', 'adjustment_out'].includes(movement.movement_type)) {
          calculatedStock -= movement.quantity;
        }
      });

      setCurrentStock(calculatedStock);
    } catch (error) {
      console.error('Error loading current stock:', error);
      setCurrentStock(0);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSearchTerm(product.name);
    setShowProductDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProduct || !selectedWarehouse || !selectedLocation) {
      alert('Please select product, warehouse, and location');
      return;
    }

    const physical = parseInt(physicalCount);
    const difference = physical - currentStock;

    if (difference === 0) {
      alert('Physical count matches system stock. No adjustment needed.');
      return;
    }

    if (!reason.trim()) {
      alert('Please provide a reason for the adjustment');
      return;
    }

    setLoading(true);

    try {
      const movementType = difference > 0 ? 'adjustment_in' : 'adjustment_out';
      const adjustmentQty = Math.abs(difference);

      // Create stock movement
      await supabase.from('stock_movements').insert([{
        product_id: selectedProduct.id,
        warehouse_id: selectedWarehouse,
        location_id: selectedLocation,
        movement_type: movementType,
        quantity: adjustmentQty,
        previous_stock: currentStock,
        new_stock: physical,
        reference_type: 'ADJUSTMENT',
        reference_id: null,
        notes: `Stock Adjustment - ${reason}`,
        created_by: user.id
      }]);

      // Update inventory position if it exists, otherwise create it
      await supabase.rpc('update_inventory_position', {
        p_product_id: selectedProduct.id,
        p_warehouse_id: selectedWarehouse,
        p_location_id: selectedLocation,
        p_quantity_change: difference
      });

      alert(`Stock adjusted successfully! ${difference > 0 ? '+' : ''}${difference} units`);
      
      // Reset form
      setSelectedProduct(null);
      setSearchTerm('');
      setPhysicalCount('');
      setReason('');
      setCurrentStock(0);
      setLoading(false);
    } catch (error) {
      console.error('Error adjusting stock:', error);
      alert('Failed to adjust stock: ' + error.message);
      setLoading(false);
    }
  };

  const difference = physicalCount ? parseInt(physicalCount) - currentStock : 0;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Edit2 className="text-amber-600" size={24} />
            <h1 className="text-xl font-bold text-gray-900">Stock Adjustment</h1>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto pb-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-bold text-amber-900">Stock Count Adjustment</p>
                <p className="text-xs text-amber-700 mt-1">
                  Use this to reconcile system stock with physical counts. All adjustments are recorded in the stock register.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-gray-200 p-6 space-y-6">
            {/* Product Search */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Product *</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  required
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowProductDropdown(true);
                    if (e.target.value === '') {
                      setSelectedProduct(null);
                    }
                  }}
                  onFocus={() => setShowProductDropdown(true)}
                  placeholder="Search by product name or barcode..."
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                />
                
                {showProductDropdown && filteredProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-300 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    {filteredProducts.map(product => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleProductSelect(product)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <div className="font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">
                          {product.stock_code && <span className="mr-3">Code: {product.stock_code}</span>}
                          {product.barcode || 'No barcode'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Warehouse & Location */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Warehouse *</label>
                <select
                  required
                  value={selectedWarehouse}
                  onChange={(e) => setSelectedWarehouse(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                >
                  <option value="">Select Warehouse</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Storage Location *</label>
                <select
                  required
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  disabled={!selectedWarehouse}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none disabled:bg-gray-100"
                >
                  <option value="">Select Location</option>
                  {storageLocations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.code} - {location.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Current Stock Display */}
            {selectedProduct && selectedWarehouse && selectedLocation && (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-1">System Stock (Theoretical)</p>
                <p className="text-3xl font-bold text-gray-900">{currentStock} units</p>
              </div>
            )}

            {/* Physical Count */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Physical Count *</label>
              <input
                type="number"
                required
                min="0"
                value={physicalCount}
                onChange={(e) => setPhysicalCount(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none text-2xl font-bold text-center"
                placeholder="Enter counted quantity"
              />
            </div>

            {/* Difference Display */}
            {physicalCount && difference !== 0 && (
              <div className={`border-2 rounded-xl p-4 ${
                difference > 0 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <p className="text-sm font-medium mb-1">
                  {difference > 0 ? 'Surplus' : 'Shortage'}
                </p>
                <p className={`text-3xl font-bold ${
                  difference > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {difference > 0 ? '+' : ''}{difference} units
                </p>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Reason for Adjustment *</label>
              <textarea
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
                rows="3"
                placeholder="e.g., Monthly stock count, Damaged goods, Theft, Found missing items..."
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => onNavigate('dashboard')}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !difference}
                className="flex-1 px-6 py-3 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 disabled:opacity-50"
              >
                {loading ? 'Adjusting...' : 'Record Adjustment'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

// ============================================
// STOCK REGISTER SCREEN  
// ============================================
const StockRegisterScreen = ({ onNavigate }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [showMovementModal, setShowMovementModal] = useState(false);

  useEffect(() => {
    loadStockMovements();
  }, [filter]);

  const viewTransferDetails = async (transferId) => {
    try {
      const { data: transfer, error } = await supabase
        .from('stock_transfers')
        .select('*')
        .eq('id', transferId)
        .single();
      
      if (error) throw error;
      
      // Fetch related data separately with proper error handling
      const fetchWithFallback = async (query) => {
        try {
          const result = await query;
          return result.data || null;
        } catch (err) {
          console.log('Fetch error:', err);
          return null;
        }
      };

      const [products, fromWarehouse, toWarehouse, fromLocation, toLocation, userProfile] = await Promise.all([
        fetchWithFallback(supabase.from('products').select('name, barcode, stock_code').eq('id', transfer.product_id).single()),
        fetchWithFallback(supabase.from('warehouses').select('name, code').eq('id', transfer.from_warehouse_id).single()),
        fetchWithFallback(supabase.from('warehouses').select('name, code').eq('id', transfer.to_warehouse_id).single()),
        fetchWithFallback(supabase.from('storage_locations').select('code, name').eq('id', transfer.from_location_id).single()),
        fetchWithFallback(supabase.from('storage_locations').select('code, name').eq('id', transfer.to_location_id).single()),
        fetchWithFallback(supabase.from('user_profiles').select('name, email').eq('id', transfer.transferred_by).single())
      ]);
      
      // Combine all data
      const enrichedTransfer = {
        ...transfer,
        products: products,
        from_warehouse: fromWarehouse,
        to_warehouse: toWarehouse,
        from_location: fromLocation,
        to_location: toLocation,
        transferred_by_user: userProfile
      };
      
      console.log('Transfer details loaded:', enrichedTransfer);
      
      setSelectedTransfer(enrichedTransfer);
      setShowTransferModal(true);
    } catch (error) {
      console.error('Error loading transfer:', error);
      alert('Failed to load transfer details: ' + error.message);
    }
  };

  const loadStockMovements = async () => {
    try {
      let query = supabase
        .from('stock_movements')
        .select(`
          *,
          products(name, barcode, stock_code),
          warehouses(name, code),
          storage_locations(code, name)
        `)
        .order('created_at', { ascending: false });

      if (filter === 'receipts') {
        query = query.in('movement_type', ['receipt', 'transfer_in', 'adjustment_in']);
      } else if (filter === 'issues') {
        query = query.in('movement_type', ['issue', 'transfer_out', 'adjustment_out']);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Fetch user profiles for created_by fields
      if (data && data.length > 0) {
        // Filter to only valid UUIDs (not emails or other strings)
        const userIds = [...new Set(
          data.map(m => m.created_by)
            .filter(id => id && id.length === 36 && id.includes('-')) // Basic UUID format check
        )];
        
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, name, email')
            .in('id', userIds);
          
          const profileMap = {};
          profiles?.forEach(p => {
            profileMap[p.id] = p;
          });
          
          // Attach user info to movements
          data.forEach(movement => {
            if (movement.created_by && movement.created_by.length === 36 && movement.created_by.includes('-')) {
              // UUID - use profile from map
              movement.user_profile = profileMap[movement.created_by];
            } else if (movement.created_by) {
              // Email string - create pseudo-profile
              movement.user_profile = {
                name: movement.created_by.split('@')[0],
                email: movement.created_by
              };
            }
          });
        } else {
          // No UUIDs, but might have email strings
          data.forEach(movement => {
            if (movement.created_by && movement.created_by.includes('@')) {
              // Email string - create pseudo-profile
              movement.user_profile = {
                name: movement.created_by.split('@')[0],
                email: movement.created_by
              };
            }
          });
        }
      }
      
      setMovements(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading movements:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <FileText className="text-primary" size={24} />
            <h1 className="text-xl font-bold text-gray-900">Stock Register</h1>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'all'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Movements
          </button>
          <button
            onClick={() => setFilter('receipts')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'receipts'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Receipts
          </button>
          <button
            onClick={() => setFilter('issues')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'issues'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Issues
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 pb-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Warehouse
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      New Stock
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {movements.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center">
                        <FileText className="mx-auto text-gray-300 mb-3" size={48} />
                        <p className="text-gray-500 font-medium">No stock movements found</p>
                      </td>
                    </tr>
                  ) : (
                    movements.map(movement => (
                      <tr key={movement.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-medium">
                            {new Date(movement.created_at).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(movement.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {movement.products?.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {movement.products?.stock_code && <span className="mr-2">Code: {movement.products.stock_code}</span>}
                            {movement.products?.barcode || 'No barcode'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {movement.warehouses?.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {movement.warehouses?.code}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {movement.storage_locations?.code}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                            ['receipt', 'transfer_in'].includes(movement.movement_type)
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {movement.movement_type.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {movement.reference_type && (
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                                  movement.reference_type === 'GRV' 
                                    ? 'bg-blue-100 text-blue-700'
                                    : movement.reference_type === 'TRANSFER'
                                    ? 'bg-purple-100 text-purple-700'
                                    : movement.reference_type === 'ORDER'
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {movement.reference_type}
                                </span>
                                {movement.reference_type === 'GRV' && movement.reference_id ? (
                                  <button
                                    onClick={() => onNavigate('viewGRV', { grvId: movement.reference_id })}
                                    className="text-xs text-primary hover:underline font-medium"
                                  >
                                    View Details →
                                  </button>
                                ) : movement.reference_type === 'TRANSFER' && movement.reference_id ? (
                                  <button
                                    onClick={() => viewTransferDetails(movement.reference_id)}
                                    className="text-xs text-primary hover:underline font-medium"
                                  >
                                    View Details →
                                  </button>
                                ) : (movement.reference_type === 'ORDER' || movement.reference_type === 'ADJUSTMENT') ? (
                                  <button
                                    onClick={() => {
                                      setSelectedMovement(movement);
                                      setShowMovementModal(true);
                                    }}
                                    className="text-xs text-primary hover:underline font-medium"
                                  >
                                    View Details →
                                  </button>
                                ) : null}
                              </div>
                            )}
                            <div className="text-sm text-gray-700">
                              {movement.notes || '-'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {movement.user_profile?.name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {movement.user_profile?.email || movement.created_by || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-bold text-gray-900">
                            {movement.quantity}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-bold text-gray-900">
                            {movement.new_stock}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Transfer Details Modal */}
      {showTransferModal && selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="text-purple-600" size={24} />
                <h2 className="text-xl font-bold text-gray-900">Stock Transfer Details</h2>
              </div>
              <button
                onClick={() => setShowTransferModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Transfer Date */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-600 font-medium mb-1">Transfer Date</p>
                <p className="text-lg font-bold text-gray-900">
                  {new Date(selectedTransfer.created_at).toLocaleDateString()} at{' '}
                  {new Date(selectedTransfer.created_at).toLocaleTimeString()}
                </p>
              </div>

              {/* Product */}
              <div>
                <p className="text-xs text-gray-600 font-medium mb-2">Product</p>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                  <p className="text-lg font-bold text-gray-900">{selectedTransfer.products?.name}</p>
                  <div className="flex gap-4 text-sm text-gray-600 mt-1">
                    {selectedTransfer.products?.stock_code && (
                      <span>Code: <span className="font-medium text-gray-900">{selectedTransfer.products.stock_code}</span></span>
                    )}
                    {selectedTransfer.products?.barcode && (
                      <span>Barcode: {selectedTransfer.products.barcode}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Transfer Route */}
              <div>
                <p className="text-xs text-gray-600 font-medium mb-2">Transfer Route</p>
                <div className="flex items-center gap-3">
                  {/* From */}
                  <div className="flex-1 bg-red-50 border-2 border-red-200 rounded-xl p-4">
                    <p className="text-xs text-red-600 font-bold mb-2">FROM</p>
                    <p className="text-sm font-bold text-gray-900">{selectedTransfer.from_warehouse?.name}</p>
                    <p className="text-xs text-gray-600">{selectedTransfer.from_warehouse?.code}</p>
                    <div className="mt-2 pt-2 border-t border-red-200">
                      <p className="text-xs text-gray-600">Location:</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTransfer.from_location?.code} - {selectedTransfer.from_location?.name}
                      </p>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0">
                    <ArrowRight className="text-gray-400" size={32} />
                  </div>

                  {/* To */}
                  <div className="flex-1 bg-green-50 border-2 border-green-200 rounded-xl p-4">
                    <p className="text-xs text-green-600 font-bold mb-2">TO</p>
                    <p className="text-sm font-bold text-gray-900">{selectedTransfer.to_warehouse?.name}</p>
                    <p className="text-xs text-gray-600">{selectedTransfer.to_warehouse?.code}</p>
                    <div className="mt-2 pt-2 border-t border-green-200">
                      <p className="text-xs text-gray-600">Location:</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTransfer.to_location?.code} - {selectedTransfer.to_location?.name}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quantity */}
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                <p className="text-xs text-purple-600 font-medium mb-1">Quantity Transferred</p>
                <p className="text-3xl font-bold text-purple-900">{selectedTransfer.quantity} units</p>
              </div>

              {/* Status & User */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-2">Status</p>
                  <span className={`inline-flex px-3 py-1 rounded-full text-sm font-bold ${
                    selectedTransfer.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedTransfer.status?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-2">Transferred By</p>
                  <p className="text-sm font-bold text-gray-900">
                    {selectedTransfer.transferred_by_user?.name || 'Unknown User'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {selectedTransfer.transferred_by_user?.email || `ID: ${selectedTransfer.transferred_by?.substring(0, 8)}...`}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedTransfer.notes && (
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-2">Notes</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <p className="text-sm text-gray-900">{selectedTransfer.notes}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowTransferModal(false)}
                className="w-full px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Movement Details Modal (for Orders/Adjustments) */}
      {showMovementModal && selectedMovement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedMovement.reference_type === 'ORDER' ? (
                  <Package className="text-orange-600" size={24} />
                ) : (
                  <Edit2 className="text-amber-600" size={24} />
                )}
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedMovement.reference_type === 'ORDER' ? 'Order Pick' : 'Stock Adjustment'} Details
                </h2>
              </div>
              <button
                onClick={() => setShowMovementModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Movement Date */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-600 font-medium mb-1">Date & Time</p>
                <p className="text-lg font-bold text-gray-900">
                  {new Date(selectedMovement.created_at).toLocaleDateString()} at{' '}
                  {new Date(selectedMovement.created_at).toLocaleTimeString()}
                </p>
              </div>

              {/* Product */}
              <div>
                <p className="text-xs text-gray-600 font-medium mb-2">Product</p>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                  <p className="text-lg font-bold text-gray-900">{selectedMovement.products?.name}</p>
                  <div className="flex gap-4 text-sm text-gray-600 mt-1">
                    {selectedMovement.products?.stock_code && (
                      <span>Code: <span className="font-medium text-gray-900">{selectedMovement.products.stock_code}</span></span>
                    )}
                    {selectedMovement.products?.barcode && (
                      <span>Barcode: {selectedMovement.products.barcode}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <p className="text-xs text-gray-600 font-medium mb-2">Location</p>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-bold text-gray-900">{selectedMovement.warehouses?.name}</p>
                  <p className="text-xs text-gray-600 mb-2">{selectedMovement.warehouses?.code}</p>
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-600">Storage Location:</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedMovement.storage_locations?.code} - {selectedMovement.storage_locations?.name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Movement Details */}
              <div className="grid grid-cols-3 gap-4">
                <div className={`rounded-xl p-4 ${
                  ['receipt', 'transfer_in', 'adjustment_in'].includes(selectedMovement.movement_type)
                    ? 'bg-green-50 border-2 border-green-200'
                    : 'bg-red-50 border-2 border-red-200'
                }`}>
                  <p className="text-xs font-medium mb-1 ${
                    ['receipt', 'transfer_in', 'adjustment_in'].includes(selectedMovement.movement_type)
                      ? 'text-green-600'
                      : 'text-red-600'
                  }">Quantity</p>
                  <p className="text-2xl font-bold ${
                    ['receipt', 'transfer_in', 'adjustment_in'].includes(selectedMovement.movement_type)
                      ? 'text-green-900'
                      : 'text-red-900'
                  }">
                    {['receipt', 'transfer_in', 'adjustment_in'].includes(selectedMovement.movement_type) ? '+' : '-'}
                    {selectedMovement.quantity}
                  </p>
                </div>

                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                  <p className="text-xs text-gray-600 font-medium mb-1">Previous</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedMovement.previous_stock}</p>
                </div>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <p className="text-xs text-blue-600 font-medium mb-1">New Stock</p>
                  <p className="text-2xl font-bold text-blue-900">{selectedMovement.new_stock}</p>
                </div>
              </div>

              {/* Movement Type */}
              <div>
                <p className="text-xs text-gray-600 font-medium mb-2">Movement Type</p>
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-bold ${
                  ['receipt', 'transfer_in', 'adjustment_in'].includes(selectedMovement.movement_type)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {selectedMovement.movement_type?.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              {/* User */}
              <div>
                <p className="text-xs text-gray-600 font-medium mb-2">Created By</p>
                <p className="text-sm font-bold text-gray-900">
                  {selectedMovement.user_profile?.name || 'Unknown'}
                </p>
                <p className="text-xs text-gray-600">
                  {selectedMovement.user_profile?.email || '-'}
                </p>
              </div>

              {/* Notes */}
              {selectedMovement.notes && (
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-2">Notes</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <p className="text-sm text-gray-900">{selectedMovement.notes}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowMovementModal(false)}
                className="w-full px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Placeholder screens
const GRVListScreen = ({ onNavigate }) => {
  const [grvs, setGrvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadGRVs();
  }, [filter]);

  const loadGRVs = async () => {
    try {
      let query = supabase
        .from('goods_received_vouchers')
        .select(`
          *,
          warehouses(name, code),
          items:grv_items(count)
        `)
        .order('received_date', { ascending: false });

      if (filter === 'completed') {
        query = query.eq('status', 'completed');
      } else if (filter === 'pending') {
        query = query.eq('status', 'pending');
      }

      const { data, error } = await query;

      if (error) throw error;
      setGrvs(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading GRVs:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Package className="text-primary" size={24} />
            <h1 className="text-xl font-bold text-gray-900">All GRVs</h1>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'all'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All GRVs
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'completed'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'pending'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 pb-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {grvs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Package className="text-gray-300 mb-4" size={64} />
              <p className="text-gray-500 font-medium text-lg mb-2">No GRVs found</p>
              <p className="text-gray-400 text-sm mb-6">Create your first goods receipt voucher</p>
              <button
                onClick={() => onNavigate('createGRV')}
                className="px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90"
              >
                Create GRV
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {grvs.map(grv => (
                <button
                  key={grv.id}
                  onClick={() => onNavigate('viewGRV', { grvId: grv.id })}
                  className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all text-left"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{grv.grv_number}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          grv.status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {grv.status?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{grv.supplier || 'No supplier'}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="text-gray-400" size={16} />
                          <span className="text-gray-700">
                            {new Date(grv.received_date).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Warehouse className="text-gray-400" size={16} />
                          <span className="text-gray-700">{grv.warehouses?.name}</span>
                        </div>
                        {grv.reference_number && (
                          <div className="flex items-center gap-2">
                            <FileText className="text-gray-400" size={16} />
                            <span className="text-gray-700">{grv.reference_number}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-right ml-4">
                      <p className="text-xs text-gray-600 mb-1">Items</p>
                      <p className="text-2xl font-bold text-primary">
                        {grv.items?.[0]?.count || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 text-primary text-sm font-medium">
                    View Details
                    <ArrowRight size={16} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const ViewGRVScreen = ({ onNavigate, grvId }) => {
  const [grv, setGrv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGRVDetails();
  }, [grvId]);

  const loadGRVDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('goods_received_vouchers')
        .select(`
          *,
          warehouses(name, code),
          items:grv_items(
            *,
            products(name, barcode, stock_code, category, subcategory),
            storage_locations(code, name)
          )
        `)
        .eq('id', grvId)
        .single();

      if (error) throw error;
      
      // Fetch user profile who created the GRV (only if created_by is a UUID)
      if (data.created_by && data.created_by.length === 36 && data.created_by.includes('-')) {
        try {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('name, email')
            .eq('id', data.created_by)
            .single();
          
          data.user_profile = profile;
        } catch (profileError) {
          console.log('Could not fetch user profile:', profileError);
          data.user_profile = null;
        }
      } else if (data.created_by) {
        // If created_by is an email string, create a pseudo-profile
        data.user_profile = {
          name: data.created_by.split('@')[0], // "superadmin" from "superadmin@foodline.com"
          email: data.created_by
        };
      }
      
      setGrv(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading GRV:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!grv) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('grvList')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-gray-900">View GRV</h1>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="mx-auto text-red-500 mb-3" size={48} />
            <p className="text-gray-900 font-medium">GRV not found</p>
          </div>
        </main>
      </div>
    );
  }

  const totalItems = grv.items?.reduce((sum, item) => sum + parseInt(item.quantity), 0) || 0;

  const handlePrintPDF = () => {
    window.print();
  };

  const handleExportToExcel = () => {
    if (!grv || !grv.items) return;
    
    // Prepare GRV header info
    const headerData = [
      ['GRV DETAILS'],
      ['GRV Number', grv.grv_number],
      ['Date', new Date(grv.created_at).toLocaleDateString()],
      ['Warehouse', grv.warehouses?.name || 'N/A'],
      ['Supplier', grv.supplier || 'N/A'],
      ['Invoice Number', grv.invoice_number || 'N/A'],
      ['Status', grv.status?.toUpperCase()],
      ['Created By', grv.user_profile?.name || 'Unknown'],
      ['Notes', grv.notes || ''],
      [],
      ['ITEMS RECEIVED'],
      ['#', 'Product Name', 'Stock Code', 'Barcode', 'Category', 'Subcategory', 'Location Code', 'Location Name', 'Quantity']
    ];
    
    // Prepare items data
    const itemsData = grv.items.map((item, index) => [
      index + 1,
      item.products?.name || 'Unknown Product',
      item.products?.stock_code || '-',
      item.products?.barcode || '-',
      item.products?.category || '-',
      item.products?.subcategory || '-',
      item.storage_locations?.code || '-',
      item.storage_locations?.name || '-',
      item.quantity
    ]);
    
    // Add totals row
    const totalQty = grv.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    itemsData.push([]);
    itemsData.push(['', '', '', '', '', '', '', 'TOTAL:', totalQty]);
    
    // Combine all data
    const allData = [...headerData, ...itemsData];
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_array ? XLSX.utils.aoa_to_array(allData) : XLSX.utils.aoa_to_sheet(allData);
    const worksheet = XLSX.utils.aoa_to_sheet(allData);
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 5 },   // #
      { wch: 35 },  // Product Name
      { wch: 15 },  // Stock Code
      { wch: 15 },  // Barcode
      { wch: 15 },  // Category
      { wch: 15 },  // Subcategory
      { wch: 12 },  // Location Code
      { wch: 20 },  // Location Name
      { wch: 10 }   // Quantity
    ];
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, 'GRV');
    
    // Generate filename with GRV number and date
    const filename = `GRV_${grv.grv_number}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10 print:hidden">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('grvList')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">GRV Details</h1>
              <p className="text-sm text-gray-600">{grv.grv_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700"
            >
              <FileSpreadsheet size={18} />
              <span className="hidden sm:inline">Export Excel</span>
            </button>
            <button
              onClick={handlePrintPDF}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
            >
              <Printer size={18} />
              <span className="hidden sm:inline">Print PDF</span>
            </button>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
              grv.status === 'completed' 
                ? 'bg-green-100 text-green-700'
                : grv.status === 'pending'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {grv.status?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-600 font-medium mb-1">Total Items</p>
            <p className="text-2xl font-bold text-blue-900">{grv.items?.length || 0}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <p className="text-xs text-green-600 font-medium mb-1">Total Units</p>
            <p className="text-2xl font-bold text-green-900">{totalItems}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium mb-1">Warehouse</p>
            <p className="text-sm font-bold text-purple-900">{grv.warehouses?.code}</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto pb-6">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* GRV Info Card */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">GRV Information</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">GRV Number</p>
                <p className="text-lg font-bold text-gray-900">{grv.grv_number}</p>
              </div>

              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">Received Date</p>
                <p className="text-lg font-bold text-gray-900">
                  {new Date(grv.received_date).toLocaleDateString()}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">Supplier</p>
                <p className="text-lg font-bold text-gray-900">{grv.supplier || '-'}</p>
              </div>

              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">Reference Number</p>
                <p className="text-lg font-bold text-gray-900">{grv.reference_number || '-'}</p>
              </div>

              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">Warehouse</p>
                <p className="text-sm font-bold text-gray-900">{grv.warehouses?.name}</p>
                <p className="text-xs text-gray-600">{grv.warehouses?.code}</p>
              </div>

              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">Created By</p>
                <p className="text-sm font-bold text-gray-900">
                  {grv.user_profile?.name || 'Unknown'}
                </p>
                <p className="text-xs text-gray-600">
                  {grv.user_profile?.email || grv.created_by}
                </p>
              </div>
            </div>

            {grv.notes && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-600 font-medium mb-2">Notes</p>
                <p className="text-sm text-gray-900">{grv.notes}</p>
              </div>
            )}
          </div>

          {/* Items List - Compact Table Format */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">Items Received</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Product</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Stock Code</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Barcode</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Location</th>
                    <th className="px-4 py-2 text-right text-xs font-bold text-gray-700 uppercase">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {grv.items?.map((item, index) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">{index + 1}</td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-gray-900">{item.products?.name}</div>
                        {(item.products?.category || item.products?.subcategory) && (
                          <div className="flex gap-1 mt-1">
                            {item.products?.category && (
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                {item.products.category}
                              </span>
                            )}
                            {item.products?.subcategory && (
                              <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                                {item.products.subcategory}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.products?.stock_code || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.products?.barcode || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-medium text-gray-900">{item.storage_locations?.code}</span>
                        <span className="text-gray-600 ml-1">{item.storage_locations?.name}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-lg font-bold text-primary">{item.quantity}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const InventoryReportScreen = ({ onNavigate }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadMovements();
  }, []);

  const loadMovements = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('stock_movements')
        .select(`
          *,
          products(id, name, barcode, stock_code),
          warehouses(id, name, code),
          storage_locations(id, name, code)
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      // Fetch order numbers for movements with reference_type = 'order'
      const orderIds = data
        ?.filter(m => m.reference_type === 'order' && m.reference_id)
        .map(m => m.reference_id) || [];

      let ordersMap = {};
      if (orderIds.length > 0) {
        const { data: orders } = await supabase
          .from('orders')
          .select('id, order_number')
          .in('id', orderIds);
        
        orders?.forEach(o => {
          ordersMap[o.id] = o.order_number;
        });
      }

      // Fetch GRV numbers for movements with reference_type = 'grv'
      const grvIds = data
        ?.filter(m => m.reference_type === 'grv' && m.reference_id)
        .map(m => m.reference_id) || [];

      let grvsMap = {};
      if (grvIds.length > 0) {
        const { data: grvs } = await supabase
          .from('goods_received_vouchers')
          .select('id, grv_number')
          .in('id', grvIds);
        
        grvs?.forEach(g => {
          grvsMap[g.id] = g.grv_number;
        });
      }

      // Enrich movements with order/grv numbers
      const enrichedMovements = data?.map(m => ({
        ...m,
        order_number: m.reference_type === 'order' ? ordersMap[m.reference_id] : null,
        grv_number: m.reference_type === 'grv' ? grvsMap[m.reference_id] : null
      })) || [];

      setMovements(enrichedMovements);
    } catch (error) {
      console.error('Error loading movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMovementTypeColor = (type) => {
    switch (type) {
      case 'receipt': return 'bg-green-100 text-green-800';
      case 'issue': return 'bg-red-100 text-red-800';
      case 'transfer_in': return 'bg-blue-100 text-blue-800';
      case 'transfer_out': return 'bg-orange-100 text-orange-800';
      case 'adjustment_in': return 'bg-purple-100 text-purple-800';
      case 'adjustment_out': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMovementTypeLabel = (type) => {
    switch (type) {
      case 'receipt': return 'Stock In';
      case 'issue': return 'Stock Out';
      case 'transfer_in': return 'Transfer In';
      case 'transfer_out': return 'Transfer Out';
      case 'adjustment_in': return 'Adjust +';
      case 'adjustment_out': return 'Adjust -';
      default: return type;
    }
  };

  const filteredMovements = movements.filter(m => {
    // Filter by type
    if (filterType !== 'all' && m.movement_type !== filterType) return false;
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchProduct = m.products?.name?.toLowerCase().includes(search);
      const matchBarcode = m.products?.barcode?.toLowerCase().includes(search);
      const matchOrder = m.order_number?.toLowerCase().includes(search);
      const matchGrv = m.grv_number?.toLowerCase().includes(search);
      const matchNotes = m.notes?.toLowerCase().includes(search);
      if (!matchProduct && !matchBarcode && !matchOrder && !matchGrv && !matchNotes) return false;
    }

    // Filter by date range
    if (dateFrom) {
      const movementDate = new Date(m.created_at).toISOString().split('T')[0];
      if (movementDate < dateFrom) return false;
    }
    if (dateTo) {
      const movementDate = new Date(m.created_at).toISOString().split('T')[0];
      if (movementDate > dateTo) return false;
    }

    return true;
  });

  const exportToExcel = () => {
    const exportData = filteredMovements.map(m => ({
      'Date': new Date(m.created_at).toLocaleString(),
      'Product': m.products?.name || '-',
      'Barcode': m.products?.barcode || '-',
      'Stock Code': m.products?.stock_code || '-',
      'Type': getMovementTypeLabel(m.movement_type),
      'Quantity': m.movement_type.includes('out') || m.movement_type === 'issue' ? -m.quantity : m.quantity,
      'Previous Stock': m.previous_stock || '-',
      'New Stock': m.new_stock || '-',
      'Warehouse': m.warehouses?.name || '-',
      'Location': m.storage_locations?.code || '-',
      'Reference': m.order_number || m.grv_number || '-',
      'Notes': m.notes || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Movements');
    XLSX.writeFile(wb, `stock_movements_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50 items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-gray-600">Loading stock movements...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Stock Movement History</h1>
              <p className="text-sm text-gray-600">{filteredMovements.length} movements</p>
            </div>
          </div>
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700"
          >
            <Download size={18} />
            Export
          </button>
        </div>
      </header>

      <div className="p-4 bg-white border-b border-gray-200 space-y-3">
        {/* Search */}
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search product, barcode, order #, GRV #..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none"
          >
            <option value="all">All Types</option>
            <option value="receipt">Stock In (Receipt)</option>
            <option value="issue">Stock Out (Issue)</option>
            <option value="transfer_in">Transfer In</option>
            <option value="transfer_out">Transfer Out</option>
            <option value="adjustment_in">Adjustment +</option>
            <option value="adjustment_out">Adjustment -</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none"
            placeholder="From"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none"
            placeholder="To"
          />
        </div>
      </div>

      <main className="flex-1 p-4 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {filteredMovements.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <Package size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No stock movements found</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date/Time</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredMovements.map((movement) => (
                      <tr key={movement.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {new Date(movement.created_at).toLocaleDateString()}<br />
                          <span className="text-gray-500 text-xs">
                            {new Date(movement.created_at).toLocaleTimeString()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{movement.products?.name || '-'}</div>
                          <div className="text-xs text-gray-500">
                            {movement.products?.barcode && <span className="mr-2">{movement.products.barcode}</span>}
                            {movement.products?.stock_code && <span className="text-blue-600">{movement.products.stock_code}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${getMovementTypeColor(movement.movement_type)}`}>
                            {getMovementTypeLabel(movement.movement_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-lg font-bold ${
                            movement.movement_type === 'issue' || movement.movement_type.includes('out') 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          }`}>
                            {movement.movement_type === 'issue' || movement.movement_type.includes('out') ? '-' : '+'}
                            {movement.quantity}
                          </span>
                          {movement.previous_stock !== null && movement.new_stock !== null && (
                            <div className="text-xs text-gray-500">
                              {movement.previous_stock} → {movement.new_stock}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {movement.order_number ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                              {movement.order_number}
                            </span>
                          ) : movement.grv_number ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                              {movement.grv_number}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{movement.warehouses?.code || '-'}</div>
                          <div className="text-xs text-gray-500">{movement.storage_locations?.code || '-'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                          {movement.notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const LowStockScreen = ({ onNavigate }) => (
  <div className="flex flex-col min-h-screen bg-gray-50">
    <header className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center gap-3">
        <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Low Stock Items</h1>
      </div>
    </header>
    <main className="flex-1 flex items-center justify-center">
      <p className="text-gray-500">Low Stock - Coming Soon</p>
    </main>
  </div>
);

// ============================================
// STOCK RECONCILIATION DASHBOARD
// ============================================
const StockReconciliationDashboard = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [data, setData] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [issues, setIssues] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  const loadReconciliationData = async () => {
    setRefreshing(true);
    
    try {
      const [grvResult, receiptsResult, issuesResult, inventoryResult] = await Promise.all([
        supabase.from('grv_items').select('quantity'),
        supabase.from('stock_movements').select('quantity').eq('movement_type', 'receipt'),
        supabase.from('stock_movements').select('quantity').eq('movement_type', 'issue'),
        supabase.from('inventory_positions').select('quantity')
      ]);

      const grvTotal = grvResult.data?.reduce((sum, r) => sum + (r.quantity || 0), 0) || 0;
      const receiptsTotal = receiptsResult.data?.reduce((sum, r) => sum + (r.quantity || 0), 0) || 0;
      const issuesTotal = issuesResult.data?.reduce((sum, r) => sum + (r.quantity || 0), 0) || 0;
      const inventoryTotal = inventoryResult.data?.reduce((sum, r) => sum + (r.quantity || 0), 0) || 0;

      const expectedInventory = receiptsTotal - issuesTotal;

      setData({
        grv: { count: grvResult.data?.length || 0, quantity: grvTotal },
        receipts: { count: receiptsResult.data?.length || 0, quantity: receiptsTotal },
        issues: { count: issuesResult.data?.length || 0, quantity: issuesTotal },
        inventory: { count: inventoryResult.data?.length || 0, quantity: inventoryTotal },
        expectedInventory,
        grvVsReceipts: grvTotal - receiptsTotal,
        inventoryVsExpected: inventoryTotal - expectedInventory
      });

      await loadReceipts();
      await loadIssues();
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading reconciliation data:', error);
    }
    
    setLoading(false);
    setRefreshing(false);
  };

  const loadReceipts = async () => {
    const { data: receiptData, error } = await supabase
      .from('stock_movements')
      .select(`
        id, quantity, created_at, reference_id, reference_type, notes,
        products (id, name, stock_code),
        warehouses (name, code),
        storage_locations (name, code)
      `)
      .eq('movement_type', 'receipt')
      .order('created_at', { ascending: false });

    if (!error && receiptData) {
      const grvIds = [...new Set(receiptData.filter(r => r.reference_id).map(r => r.reference_id))];
      
      let grvMap = {};
      if (grvIds.length > 0) {
        const { data: grvData } = await supabase
          .from('goods_received_vouchers')
          .select('id, grv_number, supplier, received_date')
          .in('id', grvIds);
        
        if (grvData) {
          grvMap = grvData.reduce((acc, grv) => {
            acc[grv.id] = grv;
            return acc;
          }, {});
        }
      }

      const enrichedReceipts = receiptData.map(r => ({
        ...r,
        grv: grvMap[r.reference_id] || null,
        grv_number: grvMap[r.reference_id]?.grv_number || 'N/A'
      }));

      setReceipts(enrichedReceipts);
    }
  };

  const loadIssues = async () => {
    const { data: issueData, error } = await supabase
      .from('stock_movements')
      .select(`
        id, quantity, created_at, reference_id, reference_type, notes,
        products (id, name, stock_code),
        warehouses (name, code),
        storage_locations (name, code)
      `)
      .eq('movement_type', 'issue')
      .order('created_at', { ascending: false });

    if (!error && issueData) {
      const enrichedIssues = issueData.map(issue => {
        const orderMatch = issue.notes?.match(/Order #([A-Z0-9-]+)/);
        const pickMatch = issue.notes?.match(/Pick #(\d+)/);
        
        return {
          ...issue,
          order_number: orderMatch ? orderMatch[1] : 'N/A',
          pick_number: pickMatch ? pickMatch[1] : 'N/A'
        };
      });

      setIssues(enrichedIssues);
    }
  };

  useEffect(() => {
    loadReconciliationData();
  }, []);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const getStatusColor = (difference) => {
    if (difference === 0) return 'bg-green-50 border-green-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusText = (difference) => {
    if (difference === 0) return 'Balanced';
    if (difference > 0) return `+${difference} excess`;
    return `${difference} short`;
  };

  const groupedReceipts = receipts.reduce((acc, receipt) => {
    const key = receipt.grv_number || 'Unknown';
    if (!acc[key]) {
      acc[key] = { grv_number: key, grv: receipt.grv, items: [], totalQty: 0 };
    }
    acc[key].items.push(receipt);
    acc[key].totalQty += receipt.quantity;
    return acc;
  }, {});

  const groupedIssues = issues.reduce((acc, issue) => {
    const key = issue.order_number || 'Unknown';
    if (!acc[key]) {
      acc[key] = { order_number: key, pick_number: issue.pick_number, items: [], totalQty: 0 };
    }
    acc[key].items.push(issue);
    acc[key].totalQty += issue.quantity;
    return acc;
  }, {});

  const filterBySearch = (items, term) => {
    if (!term) return items;
    const lowerTerm = term.toLowerCase();
    return Object.entries(items).filter(([key, group]) => 
      key.toLowerCase().includes(lowerTerm) ||
      group.items.some(item => 
        item.products?.name?.toLowerCase().includes(lowerTerm) ||
        item.products?.stock_code?.toLowerCase().includes(lowerTerm)
      )
    ).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  };

  const filteredReceipts = filterBySearch(groupedReceipts, searchTerm);
  const filteredIssues = filterBySearch(groupedIssues, searchTerm);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-blue-600" size={40} />
          <p className="text-gray-600">Loading reconciliation data...</p>
        </div>
      </div>
    );
  }

  const hasDiscrepancies = data.grvVsReceipts !== 0 || data.inventoryVsExpected !== 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Database className="text-cyan-600" size={24} />
              <h1 className="text-xl font-bold text-gray-900">Stock Reconciliation</h1>
            </div>
          </div>
          <button 
            onClick={loadReconciliationData}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-400 text-sm"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </header>

      <div className="bg-white border-b border-gray-200 px-4 sticky top-14 z-10">
        <div className="flex gap-1">
          <button
            onClick={() => { setActiveTab('summary'); setSearchTerm(''); }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'summary' ? 'border-cyan-600 text-cyan-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => { setActiveTab('receipts'); setSearchTerm(''); }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'receipts' ? 'border-cyan-600 text-cyan-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <TrendingUp size={16} />
            Receipts
          </button>
          <button
            onClick={() => { setActiveTab('issues'); setSearchTerm(''); }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'issues' ? 'border-cyan-600 text-cyan-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <TrendingDown size={16} />
            Issues
          </button>
        </div>
      </div>

      <main className="p-4">
        {activeTab === 'summary' && (
          <div className="space-y-4">
            <div className={`p-4 rounded-xl border-2 ${hasDiscrepancies ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
              <div className="flex items-center gap-3">
                {hasDiscrepancies ? (
                  <AlertCircle className="text-red-500" size={32} />
                ) : (
                  <CheckCircle className="text-green-500" size={32} />
                )}
                <div>
                  <h2 className="text-lg font-bold">{hasDiscrepancies ? 'Discrepancies Detected' : 'All Systems Balanced'}</h2>
                  <p className="text-sm text-gray-600">Last checked: {lastUpdated?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="text-blue-500" size={18} />
                  <span className="text-xs text-gray-500">GRV Items</span>
                </div>
                <p className="text-xl font-bold">{data.grv.quantity.toLocaleString()}</p>
                <p className="text-xs text-gray-400">{data.grv.count} records</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="text-green-500" size={18} />
                  <span className="text-xs text-gray-500">Receipts</span>
                </div>
                <p className="text-xl font-bold">{data.receipts.quantity.toLocaleString()}</p>
                <p className="text-xs text-gray-400">{data.receipts.count} records</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingDown className="text-orange-500" size={18} />
                  <span className="text-xs text-gray-500">Issues</span>
                </div>
                <p className="text-xl font-bold">{data.issues.quantity.toLocaleString()}</p>
                <p className="text-xs text-gray-400">{data.issues.count} records</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="text-purple-500" size={18} />
                  <span className="text-xs text-gray-500">Inventory</span>
                </div>
                <p className="text-xl font-bold">{data.inventory.quantity.toLocaleString()}</p>
                <p className="text-xs text-gray-400">{data.inventory.count} positions</p>
              </div>
            </div>

            <h3 className="text-base font-bold text-gray-800 mt-4">Reconciliation Checks</h3>
            
            <div className={`p-3 rounded-xl border-2 ${getStatusColor(data.grvVsReceipts)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {data.grvVsReceipts === 0 ? <CheckCircle className="text-green-500" size={20} /> : <X className="text-red-500" size={20} />}
                  <div>
                    <p className="font-medium text-sm">GRV Items vs Receipts</p>
                    <p className="text-xs text-gray-500">{data.grv.quantity.toLocaleString()} vs {data.receipts.quantity.toLocaleString()}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${data.grvVsReceipts === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {getStatusText(data.grvVsReceipts)}
                </span>
              </div>
            </div>

            <div className={`p-3 rounded-xl border-2 ${getStatusColor(data.inventoryVsExpected)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {data.inventoryVsExpected === 0 ? <CheckCircle className="text-green-500" size={20} /> : <X className="text-red-500" size={20} />}
                  <div>
                    <p className="font-medium text-sm">Inventory vs Expected</p>
                    <p className="text-xs text-gray-500">{data.inventory.quantity.toLocaleString()} vs {data.expectedInventory.toLocaleString()}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${data.inventoryVsExpected === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {getStatusText(data.inventoryVsExpected)}
                </span>
              </div>
            </div>

            <div className="bg-cyan-50 p-3 rounded-xl border border-cyan-200">
              <p className="text-sm text-center text-cyan-800">
                <strong>{data.receipts.quantity.toLocaleString()}</strong> (receipts) − <strong>{data.issues.quantity.toLocaleString()}</strong> (issues) = <strong>{data.expectedInventory.toLocaleString()}</strong> (expected)
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Metric</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Count</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr><td className="px-4 py-2">GRV Items (Source)</td><td className="px-4 py-2 text-right">{data.grv.count.toLocaleString()}</td><td className="px-4 py-2 text-right font-medium">{data.grv.quantity.toLocaleString()}</td></tr>
                  <tr className="bg-gray-50"><td className="px-4 py-2">Receipt Movements</td><td className="px-4 py-2 text-right">{data.receipts.count.toLocaleString()}</td><td className="px-4 py-2 text-right font-medium">{data.receipts.quantity.toLocaleString()}</td></tr>
                  <tr><td className="px-4 py-2">Issue Movements</td><td className="px-4 py-2 text-right">{data.issues.count.toLocaleString()}</td><td className="px-4 py-2 text-right font-medium">{data.issues.quantity.toLocaleString()}</td></tr>
                  <tr className="bg-gray-50"><td className="px-4 py-2">Inventory Positions</td><td className="px-4 py-2 text-right">{data.inventory.count.toLocaleString()}</td><td className="px-4 py-2 text-right font-medium">{data.inventory.quantity.toLocaleString()}</td></tr>
                  <tr className="bg-cyan-50"><td className="px-4 py-2 font-semibold">Expected Inventory</td><td className="px-4 py-2 text-right">—</td><td className="px-4 py-2 text-right font-bold text-cyan-600">{data.expectedInventory.toLocaleString()}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'receipts' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search GRV number, product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div className="bg-green-50 p-3 rounded-lg border border-green-200 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-green-800">Total Receipts</span>
                <p className="text-xs text-green-600">{Object.keys(filteredReceipts).length} GRVs</p>
              </div>
              <span className="text-xl font-bold text-green-800">{data.receipts.quantity.toLocaleString()} units</span>
            </div>

            {Object.entries(filteredReceipts)
              .sort(([, a], [, b]) => (b.items[0]?.created_at || '').localeCompare(a.items[0]?.created_at || ''))
              .map(([grvNumber, group]) => (
              <div key={grvNumber} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => toggleGroup(`receipt-${grvNumber}`)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-lg"><FileText className="text-green-600" size={18} /></div>
                    <div className="text-left">
                      <p className="font-bold text-sm text-green-700">{grvNumber}</p>
                      <p className="text-xs text-gray-500">{group.grv?.supplier || 'Supplier N/A'} • {group.grv?.received_date ? new Date(group.grv.received_date).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold text-sm text-green-600">+{group.totalQty}</p>
                      <p className="text-xs text-gray-500">{group.items.length} items</p>
                    </div>
                    {expandedGroups[`receipt-${grvNumber}`] ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                  </div>
                </button>
                
                {expandedGroups[`receipt-${grvNumber}`] && (
                  <div className="border-t border-gray-100">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {group.items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <p className="font-medium text-sm">{item.products?.name || 'Unknown'}</p>
                              <p className="text-xs text-gray-400">{item.products?.stock_code} • {item.storage_locations?.code || ''}</p>
                            </td>
                            <td className="px-4 py-2 text-right font-semibold text-green-600">+{item.quantity}</td>
                            <td className="px-4 py-2 text-right text-xs text-gray-400">{new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}

            {Object.keys(filteredReceipts).length === 0 && (
              <div className="text-center py-8 text-gray-500">No receipts found</div>
            )}
          </div>
        )}

        {activeTab === 'issues' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search order number, pick number, product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-orange-800">Total Issues</span>
                <p className="text-xs text-orange-600">{Object.keys(filteredIssues).length} Orders</p>
              </div>
              <span className="text-xl font-bold text-orange-800">{data.issues.quantity.toLocaleString()} units</span>
            </div>

            {Object.entries(filteredIssues)
              .sort(([, a], [, b]) => (b.items[0]?.created_at || '').localeCompare(a.items[0]?.created_at || ''))
              .map(([orderNumber, group]) => (
              <div key={orderNumber} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => toggleGroup(`issue-${orderNumber}`)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-100 p-2 rounded-lg"><Package className="text-orange-600" size={18} /></div>
                    <div className="text-left">
                      <p className="font-bold text-sm text-orange-700">{orderNumber}</p>
                      <p className="text-xs text-gray-500">Pick #{group.pick_number} • {group.items[0]?.created_at ? new Date(group.items[0].created_at).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold text-sm text-orange-600">−{group.totalQty}</p>
                      <p className="text-xs text-gray-500">{group.items.length} items</p>
                    </div>
                    {expandedGroups[`issue-${orderNumber}`] ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                  </div>
                </button>
                
                {expandedGroups[`issue-${orderNumber}`] && (
                  <div className="border-t border-gray-100">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {group.items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <p className="font-medium text-sm">{item.products?.name || 'Unknown'}</p>
                              <p className="text-xs text-gray-400">{item.products?.stock_code} • {item.storage_locations?.code || ''}</p>
                            </td>
                            <td className="px-4 py-2 text-right font-semibold text-orange-600">−{item.quantity}</td>
                            <td className="px-4 py-2 text-right text-xs text-gray-400">{new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}

            {Object.keys(filteredIssues).length === 0 && (
              <div className="text-center py-8 text-gray-500">No issues found</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// ============================================
// GRV REPORT SCREEN - Full Item Register
// ============================================
const GRVReportScreen = ({ onNavigate }) => {
  const [grvs, setGrvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGrvs, setExpandedGrvs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadAllGRVs();
  }, []);

  const loadAllGRVs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('goods_received_vouchers')
        .select(`
          *,
          warehouses(name, code),
          items:grv_items(
            *,
            products(name, barcode, stock_code, category, subcategory),
            storage_locations(code, name)
          )
        `)
        .order('received_date', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;

      setGrvs(data || []);
      // Expand all by default
      const expanded = {};
      (data || []).forEach(g => { expanded[g.id] = true; });
      setExpandedGrvs(expanded);
    } catch (error) {
      console.error('Error loading GRVs for report:', error);
    }
    setLoading(false);
  };

  const toggleGrv = (id) => {
    setExpandedGrvs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const e = {};
    filteredGrvs.forEach(g => { e[g.id] = true; });
    setExpandedGrvs(e);
  };

  const collapseAll = () => setExpandedGrvs({});

  // Filter logic
  const filteredGrvs = grvs.filter(grv => {
    // Status filter
    if (statusFilter !== 'all' && grv.status !== statusFilter) return false;

    // Date range filter
    if (dateFrom) {
      const grvDate = new Date(grv.received_date || grv.created_at).toISOString().split('T')[0];
      if (grvDate < dateFrom) return false;
    }
    if (dateTo) {
      const grvDate = new Date(grv.received_date || grv.created_at).toISOString().split('T')[0];
      if (grvDate > dateTo) return false;
    }

    // Search filter
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const matchSearch =
        grv.grv_number?.toLowerCase().includes(s) ||
        grv.supplier?.toLowerCase().includes(s) ||
        grv.warehouses?.name?.toLowerCase().includes(s) ||
        grv.items?.some(item =>
          item.products?.name?.toLowerCase().includes(s) ||
          item.products?.stock_code?.toLowerCase().includes(s) ||
          item.products?.barcode?.toLowerCase().includes(s)
        );
      if (!matchSearch) return false;
    }

    return true;
  });

  // Stats
  const totalGRVs = filteredGrvs.length;
  const totalLineItems = filteredGrvs.reduce((s, g) => s + (g.items?.length || 0), 0);
  const totalQtyReceived = filteredGrvs.reduce((s, g) => s + (g.items?.reduce((si, it) => si + (it.quantity || 0), 0) || 0), 0);
  const uniqueSuppliers = [...new Set(filteredGrvs.map(g => g.supplier).filter(Boolean))];

  // Export to Excel
  const handleExportExcel = () => {
    if (filteredGrvs.length === 0) return;

    const rows = [];
    // Header
    rows.push(['GRV Number', 'Date', 'Time', 'Supplier', 'Warehouse', 'Status', 'Received By', '#', 'Product Name', 'Stock Code', 'Barcode', 'Category', 'Subcategory', 'Location Code', 'Location Name', 'Qty Received', 'Batch Number', 'Expiry Date']);

    filteredGrvs.forEach(grv => {
      const d = new Date(grv.received_date || grv.created_at);
      const dateStr = d.toLocaleDateString('en-ZA');
      const timeStr = d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });

      (grv.items || []).forEach((item, idx) => {
        rows.push([
          grv.grv_number,
          dateStr,
          timeStr,
          grv.supplier || 'N/A',
          grv.warehouses?.name || 'N/A',
          grv.status || '',
          grv.received_by || grv.created_by || '',
          idx + 1,
          item.products?.name || 'Unknown',
          item.products?.stock_code || '',
          item.products?.barcode || '',
          item.products?.category || '',
          item.products?.subcategory || '',
          item.storage_locations?.code || '',
          item.storage_locations?.name || '',
          item.quantity,
          item.batch_number || '',
          item.expiry_date || ''
        ]);
      });
    });

    // Summary row
    rows.push([]);
    rows.push(['TOTALS', '', '', '', '', '', '', '', '', '', '', '', '', '', '', totalQtyReceived, '', '']);
    rows.push([`${totalGRVs} GRVs`, '', '', `${uniqueSuppliers.length} Suppliers`, '', '', '', '', `${totalLineItems} Line Items`, '', '', '', '', '', '', '', '', '']);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 16 }, { wch: 12 }, { wch: 8 }, { wch: 22 }, { wch: 18 },
      { wch: 10 }, { wch: 24 }, { wch: 4 }, { wch: 35 }, { wch: 12 },
      { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 18 },
      { wch: 10 }, { wch: 14 }, { wch: 12 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'GRV Report');
    XLSX.writeFile(wb, `GRV_Full_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10 print:hidden">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <ClipboardList className="text-green-600" size={24} />
              <div>
                <h1 className="text-xl font-bold text-gray-900">GRV Report</h1>
                <p className="text-xs text-gray-500">Full Goods Received Register</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadAllGRVs}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 text-sm"
            >
              <RefreshCw size={16} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleExportExcel}
              disabled={filteredGrvs.length === 0}
              className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 text-sm disabled:opacity-50"
            >
              <FileSpreadsheet size={16} />
              <span className="hidden sm:inline">Export Excel</span>
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 text-sm"
            >
              <Printer size={16} />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search GRV, supplier, product, barcode..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            placeholder="From"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            placeholder="To"
          />
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {['all', 'completed', 'pending'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                  statusFilter === s
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <button onClick={expandAll} className="px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg">
            Expand All
          </button>
          <button onClick={collapseAll} className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">
            Collapse All
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto pb-6">
        <div className="max-w-7xl mx-auto space-y-4">

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Total GRVs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalGRVs}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-green-200 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Line Items</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{totalLineItems}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-blue-200 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Qty Received</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{totalQtyReceived.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-purple-200 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Suppliers</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">{uniqueSuppliers.length}</p>
            </div>
          </div>

          {/* GRV Cards */}
          {filteredGrvs.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <Package className="mx-auto text-gray-300 mb-4" size={64} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No GRVs Found</h3>
              <p className="text-gray-600">Try adjusting your search or date filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredGrvs.map(grv => {
                const d = new Date(grv.received_date || grv.created_at);
                const dateStr = d.toLocaleDateString('en-ZA', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
                const timeStr = d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
                const itemCount = grv.items?.length || 0;
                const qtyTotal = grv.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;
                const expanded = expandedGrvs[grv.id];

                return (
                  <div key={grv.id} className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                    {/* GRV Header */}
                    <button
                      onClick={() => toggleGrv(grv.id)}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="text-green-600" size={20} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-gray-900">{grv.grv_number}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              grv.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {grv.status?.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5 truncate">
                            {grv.supplier || 'No supplier'} • {grv.warehouses?.name || 'N/A'} • {dateStr} at {timeStr}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                        <div className="text-right">
                          <p className="text-xs text-gray-400 font-medium">Items / Qty</p>
                          <p className="font-bold text-green-600">{itemCount} / {qtyTotal.toLocaleString()}</p>
                        </div>
                        {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                      </div>
                    </button>

                    {/* Expanded Detail */}
                    {expanded && (
                      <div className="border-t border-gray-100">
                        {/* GRV Metadata */}
                        <div className="px-5 py-2 bg-gray-50 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
                          <span><strong>GRV #:</strong> {grv.grv_number}</span>
                          <span><strong>Date:</strong> {dateStr}</span>
                          <span><strong>Time:</strong> {timeStr}</span>
                          <span><strong>Supplier:</strong> {grv.supplier || 'N/A'}</span>
                          <span><strong>Warehouse:</strong> {grv.warehouses?.name || 'N/A'} ({grv.warehouses?.code || ''})</span>
                          <span><strong>Received By:</strong> {grv.received_by || grv.created_by || 'N/A'}</span>
                          {grv.reference_number && <span><strong>Ref:</strong> {grv.reference_number}</span>}
                          {grv.invoice_number && <span><strong>Invoice:</strong> {grv.invoice_number}</span>}
                          {grv.notes && <span><strong>Notes:</strong> {grv.notes}</span>}
                        </div>

                        {/* Items Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-100 border-y border-gray-200">
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase w-8">#</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">Product</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">Stock Code</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">Barcode</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">Category</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">Location</th>
                                <th className="px-4 py-2 text-right text-xs font-bold text-gray-500 uppercase">Qty</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">Batch</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">Expiry</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {(grv.items || []).map((item, idx) => (
                                <tr key={item.id || idx} className="hover:bg-green-50/30">
                                  <td className="px-4 py-2 text-gray-400 font-medium">{idx + 1}</td>
                                  <td className="px-4 py-2 font-semibold text-gray-800">{item.products?.name || 'Unknown'}</td>
                                  <td className="px-4 py-2 text-gray-600 font-mono text-xs">{item.products?.stock_code || '—'}</td>
                                  <td className="px-4 py-2 text-gray-600 font-mono text-xs">{item.products?.barcode || '—'}</td>
                                  <td className="px-4 py-2 text-gray-600 text-xs">{item.products?.category || '—'}</td>
                                  <td className="px-4 py-2">
                                    <span className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded">
                                      {item.storage_locations?.code || '—'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-right font-bold text-green-600">{item.quantity}</td>
                                  <td className="px-4 py-2 text-gray-500 text-xs">{item.batch_number || '—'}</td>
                                  <td className="px-4 py-2 text-gray-500 text-xs">
                                    {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-ZA') : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-green-50 border-t-2 border-green-200">
                                <td colSpan={6} className="px-4 py-2 text-right font-bold text-gray-700 text-xs uppercase">
                                  Total for {grv.grv_number}
                                </td>
                                <td className="px-4 py-2 text-right font-extrabold text-green-700 text-base">
                                  {qtyTotal.toLocaleString()}
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Grand Total Footer */}
          {filteredGrvs.length > 0 && (
            <div className="bg-green-700 text-white rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-green-200 text-xs font-semibold uppercase">Grand Total — All Filtered GRVs</p>
                <p className="text-xl font-bold mt-1">
                  {totalGRVs} GRVs • {totalLineItems} Line Items • {totalQtyReceived.toLocaleString()} Units Received
                </p>
              </div>
              <div className="text-sm text-green-200">
                Report generated: {new Date().toLocaleString('en-ZA')}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// Print styles for GRV PDF
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @media print {
      @page {
        margin: 1cm;
        size: A4;
      }
      
      body {
        background: white !important;
      }
      
      .print\\:hidden {
        display: none !important;
      }
      
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `;
  document.head.appendChild(style);
}

export default WarehouseApp;
